import moment, { type Moment } from "moment/moment";
import type { MaybePromise, Override } from "../../../../types/internal/utility";
import type { LocalDatabaseCustomer } from "../../../Data/_Bases/BaseCustomerRepository";
import type { BaseLoyaltyRepository, LocalDatabaseLoyalty } from "../../../Data/_Bases/BaseLoyaltyRepository";
import type {
    BasePriceListRepository,
    LocalDatabasePriceList,
    LocalDatabasePriceListRule,
    LocalDatabasePriceListWithPricesRule,
} from "../../../Data/_Bases/BasePriceListRepository";
import type { BasePriceSetRepository } from "../../../Data/_Bases/BasePriceSetRepository";
import type { BaseProductRepository, LocalDatabaseProduct } from "../../../Data/_Bases/BaseProductRepository";
import type {
    BasePromotionRepository,
    LocalDatabasePromotion,
    LocalDatabasePromotionCriteria,
} from "../../../Data/_Bases/BasePromotionRepository";
import type { SaleProduct } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import NotFoundException from "../../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import Cloner from "../../Generic/Cloner";
import { scaleToDecimalPlaces } from "../../Generic/Numbers";
import ArrayPool from "../../Generic/Pools/ArrayPool";
import ObjectPool from "../../Generic/Pools/ObjectPool";
import { getLogLevel, LogLevel } from "../../Platform/Logging";
import { determineLocalCost } from "../Products/CostUtilities";
import { AdvancedPromotionCache } from "./AdvancedPromotionCache";
import { Knapsack, type KnapsackObject } from "./Knapsack";
import { PromotionCache } from "./PromotionCache";
import {
    PromotionCalculation,
} from "./PromotionCalculation";
import { FatalPromotionException, PromotionException } from "./PromotionErrors";
import type {
    PromotionCalculationResult,
    PromotionCalculationResultBranch,
    PromotionCalculationResultSingle,
    PromotionManagerProduct,
    PromotionManagerSettings,
} from "./PromotionTypes";
import {
    type GroupedProduct,
    groupProductsByUUID,
    isSpecialProduct,
    type SalePromotionProduct,
    type SalePromotionProductPrice,
} from "./PromotionUtilities";
import { RebateCalculationManager } from "./RebateCalculationManager";

export interface ComparisonProduct {
    uuid: string;
    quantity: number;
    edited: boolean;
    familyId: string | null;
    special: SalePromotionProduct["special"];
    type: SalePromotionProduct["type"];
    prices: SalePromotionProduct["prices"];
    promotions: SalePromotionProduct["promotions"];
    case_quantity: number;
    createdFromBarcodeTemplate?: boolean;
    price_points?: SalePromotionProduct["price_points"];
    indexes?: Array<number>;
}

class PromotionManager {
    public static onGreedyCalculationUsed: () => void = () => { /* no-op */ };
    public currentPromotionCalculation: false | number;
    public readonly settings: PromotionManagerSettings;
    public globalProducts: Array<any>;
    public cache!: PromotionCache;
    public productRepository: BaseProductRepository;
    public currentOutlet: string | null = null;
    public currentCustomer: LocalDatabaseCustomer | null = null;
    protected formatFunction: (products: Array<any>) => void = () => { /* no-op */ };
    protected currentFormatFunction: (products: Array<any>) => void = this.formatFunction;
    protected currentTime: Moment;
    public promotionRepository: BasePromotionRepository;
    protected priceListRepository!: BasePriceListRepository;
    protected loyaltyRepository: BaseLoyaltyRepository;
    protected priceSetRepository: BasePriceSetRepository;
    protected isTimeoutEnabled = true;
    protected classificationCache: undefined | {
        products: Array<string>;
        brands: Array<string>;
        categories: Array<string>;
        families: Array<string>;
        tags: Array<string>;
    };
    protected productCache: Record<string, LocalDatabaseProduct> = {};
    protected priceCache: Record<string, {
        price: number;
        pricePoints: Array<SalePromotionProductPrice>;
    }> = {};
    protected priceSetCache: { valid: boolean; parents: Record<string, string | null>; } = {
        valid  : false,
        parents: {},
    };

    /**
     * Constructor for the Promotion Manager
     */
    constructor(settings: Partial<PromotionManagerSettings>) {
        // Set generic variables
        this.currentPromotionCalculation = false;
        this.currentTime = moment();
        this.productRepository = {} as unknown as BaseProductRepository; // TODO: Probably replace this
        this.promotionRepository = {} as unknown as BasePromotionRepository; // TODO: Probably replace this too
        this.loyaltyRepository = {} as unknown as BaseLoyaltyRepository; // TODO: Probably replace this too
        this.priceSetRepository = {} as unknown as BasePriceSetRepository; // TODO: Probably replace this too

        // Default settings
        this.settings = {
            price_set            : false,
            use_quantity_rate    : false,
            cross_promotion_count: true,
            earn_rate            : 0,
            redemption_rate      : 0,
            family_distribution  : "match_price_points",
            decimal_places       : 2,
            rounding_mode        : "redistribute",
        };

        // General variables
        this.globalProducts = [];

        // Update the settings with the provided options
        this.updateSettings(settings);

        // Setup the cache
        this.setupCache();
    }

    /**
     * Update the settings for this manager
     * @param settings
     */
    public updateSettings(settings: Partial<PromotionManagerSettings>): void {
        for(const i in settings) {
            if(!Object.prototype.hasOwnProperty.call(settings, i)) continue;

            const setting = settings[i as keyof PromotionManagerSettings];

            if(typeof setting === "undefined") continue;

            this.settings[i as keyof PromotionManagerSettings] = setting as never; // Need to find out how to fix this
        }
    }

    /**
     * Set the current time to determine when promotions should be declared valid
     * @param time
     */
    public setCurrentTime(time: Moment): void {
        this.currentTime = time;
    }

    /**
     * Set the product repository to an external source
     * @param repository
     */
    public setProductRepository(repository: BaseProductRepository): void {
        this.productRepository = repository;
    }

    /**
     * Set the promotion repository to an external source
     * @param repository
     */
    public setPromotionRepository(repository: BasePromotionRepository): void {
        this.promotionRepository = repository;

        // Update the promotion cache
        this.setupCache();
    }

    public setPriceListRepository(repository: BasePriceListRepository): void {
        this.priceListRepository = repository;
    }

    /**
     * Set the loyalty repository to an external source
     * @param repository
     */
    public setLoyaltyRepository(repository: BaseLoyaltyRepository): void {
        this.loyaltyRepository = repository;
    }

    /**
     * Set the price set repository to an external store
     * @param repository
     */
    public setPriceSetRepository(repository: BasePriceSetRepository): void {
        this.priceSetRepository = repository;
    }

    /**
     * Setup the cache for the promotions
     * @private
     */
    protected setupCache(): void {
        this.cache = new PromotionCache(this, 1);
    }

    /**
     * Set whether the timeout is enabled or not
     * @param {boolean} enabled
     */
    public setTimeoutEnabled(enabled = true): void {
        this.isTimeoutEnabled = enabled;
    }

    /**
     * Merge separate lines to create products built for comparisons
     */
    protected createProductComparisons(products: Array<SalePromotionProduct>): [ Array<ComparisonProduct>, boolean ] {
        const comparison = ArrayPool.Get<ComparisonProduct>();
        const comparisonMap = ObjectPool.Get<Record<string, number>>();
        let haveMerged = false;

        for(let i = 0, l = products.length; i < l; i++) {
            const shouldBeUnique = products[i].createdFromBarcodeTemplate;

            let key = `${products[i].uuid}|${products[i].edited}|${products[i].special}|${products[i].quantity <= 0}`;

            if(shouldBeUnique) {
                key = `${key}|${i}`;
            }

            if(typeof comparisonMap[key] === "undefined") {
                comparisonMap[key] = comparison.length;
                comparison.push({
                    uuid                      : products[i].uuid,
                    quantity                  : products[i].quantity,
                    edited                    : products[i].edited,
                    familyId                  : products[i].familyId,
                    special                   : products[i].special,
                    type                      : products[i].type,
                    case_quantity             : products[i].case_quantity,
                    prices                    : Cloner.Clone(products[i].prices),
                    promotions                : Cloner.Clone(products[i].promotions),
                    price_points              : Cloner.Clone(products[i].price_points),
                    indexes                   : [ i ],
                    createdFromBarcodeTemplate: products[i].createdFromBarcodeTemplate,
                });
            } else {
                comparison[comparisonMap[key]].quantity += products[i].quantity;
                comparison[comparisonMap[key]].indexes!.push(i);
                haveMerged = true;
            }
        }

        return [ comparison, haveMerged ];
    }

    /**
     * Redistribute the comparison products back out into the sale items
     */
    protected distributeComparison(
        products: Array<SalePromotionProduct>,
        comparison: Array<ComparisonProduct>,
        idealDistribution: Record<string, Record<number, number>>
    ): void {
        // Update the normal prices for when distributed across the sale
        this.updateAllFamiliesPrices(products, false);

        for(const product of comparison) {
            // In the future we could potentially speed this up by ignoring the distribution of we're only looking at
            // items with a single index in them.
            if((!product.createdFromBarcodeTemplate && product.special) || product.edited) {
                continue;
            }

            // Store the normal prices before redistributing again
            const previousNormal = ObjectPool.Get<Record<number, number>>();

            for(const index of product.indexes!) {
                previousNormal[index] = products[index].prices.normal;
            }

            const multiplier = product.quantity < 0 ? -1 : 1;

            // Distribute the allocation
            this.distributeGroupPrices({
                price     : product.prices.price - product.prices.additionalPrice[1],
                quantity  : product.quantity,
                products  : product.indexes!,
                isNegative: product.quantity < 0,
            }, products, product.prices.price / product.quantity, "distribution", false, idealDistribution);

            for(const index of product.indexes!) {
                for(const price of products[index].prices.compare!) {
                    if(price.source === "distribution") {
                        // Set the distribution as the price
                        products[index].prices.price = price.price * products[index].quantity * multiplier;
                        products[index].prices.unlockedPrice = price.price * products[index].quantity * multiplier;

                        break;
                    }
                }

                // Restore the old normal price which was already distributed
                products[index].prices.normal = previousNormal[index];

                // Ensure we're using the latest version of the promotions object
                products[index].promotions = {
                    ...product.promotions,
                };

                // Ensure we're passing back the items used
                products[index].prices.used = product.prices.used;
            }
        }

        // Loop through all the products and ensure they're using the correct number of decimal places
        let totalDifference = 0;
        let normalDifference = 0;

        for(let i = 0, l = products.length; i < l; i++) {
            const totalRounded = scaleToDecimalPlaces(products[i].prices.price, this.settings.decimal_places);
            const normalRounded = scaleToDecimalPlaces(products[i].prices.normal, this.settings.decimal_places);

            totalDifference += totalRounded - products[i].prices.price;
            normalDifference += normalRounded - products[i].prices.normal;

            products[i].prices.price = totalRounded;
            products[i].prices.normal = normalRounded;
        }

        totalDifference = scaleToDecimalPlaces(totalDifference, this.settings.decimal_places + 1);

        if(Math.abs(totalDifference) >= 1 / Math.pow(10, this.settings.decimal_places)) {
            if(totalDifference !== 0) {
                products[products.length - 1].prices.price -= totalDifference;
            }
        }

        normalDifference = scaleToDecimalPlaces(normalDifference, this.settings.decimal_places + 1);

        if(Math.abs(normalDifference) >= 1 / Math.pow(10, this.settings.decimal_places)) {
            if(normalDifference !== 0) {
                products[products.length - 1].prices.normal -= normalDifference;
            }
        }
    }

    /**
     * Determine the promotion distributions that should be used if the items are distributed in an "ideal" fashion.
     * Ideal is essentially if the quantity is equal to the quantity of the promotion that is applied
     */
    protected async getIdealPromotionDistributions(
        products: Array<ComparisonProduct>
    ): Promise<Record<string, Record<number, number>>> {
        // Distributions are in the format `Record<product, Record<quantity, price>>`
        const distributions = ObjectPool.Get<Record<string, Record<number, number>>>();
        const promotionCache = ObjectPool.Get<Record<string, LocalDatabasePromotion>>();

        // We want to loop through each product, calculate the total used for that promotion and then find any promotion
        // which was used that also matches (or is a multiple of) the quantity which we'll then use as the "ideal
        // distribution"
        for(const product of products) {
            const dbProduct = this.productCache[product.uuid];

            if(!dbProduct) {
                continue;
            }

            const promotionsByQuantity = ObjectPool.Get<Record<string, number>>();

            if(product.prices.used) {
                for(const used of product.prices.used) {
                    if(typeof product.promotions[used] === "undefined") {
                        // Not a promotion, ignore it
                        continue;
                    }

                    if(typeof promotionsByQuantity[used] === "undefined") {
                        promotionsByQuantity[used] = 0;
                    }

                    promotionsByQuantity[used]++;
                }
            }

            for(const id in promotionsByQuantity) {
                if(!Object.hasOwn(promotionsByQuantity, id)) {
                    continue;
                }

                if(typeof promotionCache[id] === "undefined") {
                    try {
                        promotionCache[id] = await this.promotionRepository.get(id);
                    } catch(e) {
                        if(e instanceof NotFoundException) {
                            // Another type of price
                            continue;
                        }

                        throw e;
                    }
                }

                // Get each criteria this product belongs to and add the quantity -> price to the ideal
                // distribution list with the lowest price winning
                for(let i = 0, l = promotionCache[id].criteria.length; i < l; i++) {
                    const criteria = promotionCache[id].criteria[i];
                    let price = null;

                    if(criteria.buy_amount_type !== "quantity") {
                        continue;
                    }

                    if(criteria.type === "sell-item") {
                        price = criteria.buy_amount * criteria.discount_amount;
                    } else if(criteria.type === "sell-total") {
                        price = criteria.discount_amount;
                    }

                    if(price === null) {
                        continue;
                    }

                    let inCriteria = this.cache.checkCriteriaCache(
                        product.uuid,
                        i,
                        id
                    );

                    if(inCriteria === false) {
                        inCriteria = PromotionCalculation.isPromotionInCriteria(
                            dbProduct,
                            criteria
                        );
                    }

                    if(inCriteria === "include") {
                        if(typeof distributions[product.uuid] === "undefined") {
                            distributions[product.uuid] = ObjectPool.Get();
                        }

                        distributions[product.uuid][criteria.buy_amount] = Math.min(
                            price,
                            distributions[product.uuid][criteria.buy_amount] ?? Infinity
                        );
                    }
                }
            }

            ObjectPool.Release(promotionsByQuantity);
        }

        // Need to leave the promotionCache up to the garbage collector otherwise we break internal caches
        // ObjectPool.Release(promotionCache);

        return distributions;
    }

    /**
     * Determine the best price for all products
     */
    protected determineBestPrice(
        products: Array<ComparisonProduct>,
        priceListOverride: boolean,
        minimumQuantities: Record<string, number>,
        excludedPromotions: Array<string> = [],
        alreadyCheckFamily = false
    ): boolean {
        // Loop through each product
        const beatenFamilyPrices = ArrayPool.Get<string>();
        const usedFamilyPriceIndexes = ArrayPool.Get<number>();
        const usedQuantities = ObjectPool.Get<Record<string, number>>();
        const everydayQuantities = ObjectPool.Get<Record<number, number>>();
        // let rounding = 0;
        // let applyRoundingTo = 0;

        for(let i = 0, l = products.length; i < l; i++) {
            // Check if we have a price list price
            let useOnlyPriceList = false;

            if(priceListOverride) {
                for(let j = 0, n = products[i].prices.compare!.length; j < n; j++) {
                    if(products[i].prices.compare![j].source === "priceList") {
                        useOnlyPriceList = true;
                        break;
                    }
                }
            }

            // Check if the quantity is negative
            let negative = 1;

            if(products[i].quantity < 0) {
                negative = -1;
            }

            // Reset the used
            products[i].prices.used = ArrayPool.Get();

            // Use the knapsack algorithm to get the best price
            const possibilities = Knapsack.getObject<number>();
            const everydayPrices = [];

            let hasFamilyPrice = false;

            // Ensure family prices are first as those are preferred
            if(!useOnlyPriceList) {
                products[i].prices.compare!.sort((a, b) => {
                    if(a.source === b.source) {
                        return 0;
                    }

                    if(a.source === "family") {
                        return -1;
                    }

                    if(b.source === "family") {
                        return 1;
                    }

                    if(a.source === "everyday") {
                        return -1;
                    }

                    if(b.source === "everyday") {
                        return 1;
                    }

                    return 0;
                });
            }

            for(let j = 0, n = products[i].prices.compare!.length; j < n; j++) {
                if(
                    products[i].prices.compare![j].source === "everyday" ||
                    products[i].prices.compare![j].source === "family"
                ) {
                    everydayPrices.push(products[i].prices.compare![j]);

                    if(products[i].prices.compare![j].source === "family" && products[i].familyId) {
                        hasFamilyPrice = true;
                    }
                }

                if(
                    useOnlyPriceList &&
                    products[i].prices.compare![j].source !== "priceList"
                ) {
                    continue;
                }

                if(excludedPromotions.includes(products[i].prices.compare![j].source)) {
                    continue;
                }

                if(typeof products[i].prices.compare![j].compare !== "undefined") {
                    possibilities.prices.push(products[i].prices.compare![j].compare!);
                } else {
                    possibilities.prices.push(products[i].prices.compare![j].price);
                }

                possibilities.quantities.push(products[i].prices.compare![j].quantity);
                possibilities.identifiers.push(j);
                possibilities.maxQuantities.push(Infinity);
            }

            // Solve the knapsack algorithm
            const ks = Knapsack.solve(possibilities, products[i].quantity * negative, true);

            if(typeof ks.solution === "undefined") {
                if(!products[i].edited) {
                    // Revert to old way, however this shouldn't happen
                    products[i].prices.price = PromotionManager.calculateHighMixPrice(
                        products[i].prices.compare!,
                        products[i].quantity * negative
                    ) * negative;
                }

                continue;
            }

            ks.solution = ks.solution / ks.scale;

            if(everydayPrices.length && ks.usedQuantity !== products[i].quantity * negative) {
                const quantityDiff = (products[i].quantity * negative) - ks.usedQuantity;

                ks.solution += everydayPrices[0].price / everydayPrices[0].quantity * quantityDiff;
            }

            // Replace the price if required
            let solutionPrice = ks.solution;

            for(let j = 0, n = ks.used.length; j < n; j++) {
                if(typeof products[i].prices.compare![ks.used[j]].compare !== "undefined") {
                    solutionPrice -= products[i].prices.compare![ks.used[j]].compare!;
                    solutionPrice += products[i].prices.compare![ks.used[j]].price;
                }

                if(typeof usedQuantities[products[i].prices.compare![ks.used[j]].source] === "undefined") {
                    usedQuantities[products[i].prices.compare![ks.used[j]].source] = 0;
                }

                usedQuantities[products[i].prices.compare![ks.used[j]].source] +=
                    products[i].prices.compare![ks.used[j]].quantity;
            }

            /*
            SHOPFRONT-4970: previously we were rounding the price here as this was the final part of the calculation,
            but the rounding now occurs in the comparison distribution.

            // Round the solution price
            const newPrice = scaleToDecimalPlaces(solutionPrice, this.settings.decimal_places);

            rounding += solutionPrice - newPrice;
            solutionPrice = newPrice;
             */

            // Set the price
            products[i].prices.unlockedPrice = solutionPrice + products[i].prices.additionalPrice[0];

            if(!products[i].edited) {
                products[i].prices.price = solutionPrice + products[i].prices.additionalPrice[1];
            }

            // Add to the used
            let usedFamilyPrice = false;
            let usedPromotionalPrice = false;
            let onlyUsedFamilyPrices = true;
            const everydayUsed = ObjectPool.Get<Record<string, number>>();
            let potentialMinimumQuantity = 0;

            for(let j = 0, n = ks.used.length; j < n; j++) {
                if(!products[i].edited) {
                    products[i].prices.used!.push(products[i].prices.compare![ks.used[j]].source);
                }

                const sources = products[i].prices.compare![ks.used[j]].source.split(",");

                for(let k = 0, c = sources.length; k < c; k++) {
                    // The key is the raw promotion ID, the value is the internal mapping to use for the minimum
                    // quantity check. This is typically only different when an optional-only promotion has been used
                    // with cross-promotion-count disabled
                    const sourcesToCheck = {} as Record<string, string>;

                    if(sources[k].includes(":")) {
                        sourcesToCheck[sources[k].split(":")[0]] = sources[k];
                    } else {
                        const grouped = sources[k].split("|");

                        for(const source of grouped) {
                            sourcesToCheck[source] = source;
                        }
                    }

                    for(const source in sourcesToCheck) {
                        if(!Object.hasOwn(sourcesToCheck, source)) continue;

                        if(source === "family") {
                            usedFamilyPrice = true;
                        } else {
                            onlyUsedFamilyPrices = false;
                        }

                        if(typeof products[i].promotions[source] !== "undefined") {
                            usedPromotionalPrice = true;
                            products[i].promotions[source].active = true;
                            products[i].promotions[source].quantity += products[i].prices.compare![ks.used[j]].quantity;

                            // noinspection SuspiciousTypeOfGuard
                            if(typeof products[i].prices.compare![ks.used[j]].everyday === "number") {
                                everydayUsed[source] = products[i].prices.compare![ks.used[j]].everyday as number;
                                usedFamilyPrice = true;
                            }
                        }

                        if(
                            !this.settings.cross_promotion_count &&
                            typeof minimumQuantities[sourcesToCheck[source]] === "number"
                        ) {
                            potentialMinimumQuantity += products[i].prices.compare![ks.used[j]].quantity;
                            usedFamilyPrice = true;
                        }
                    }
                }
            }

            if(
                !this.settings.cross_promotion_count &&
                potentialMinimumQuantity > 0 &&
                Object.keys(everydayUsed).length === 0
            ) {
                const promotionUsed = products[i].quantity - potentialMinimumQuantity;

                everydayUsed[products[i].uuid] = promotionUsed;
                usedFamilyPrice = true;
            }

            if(!alreadyCheckFamily) {
                if(hasFamilyPrice && !usedFamilyPrice) {
                    beatenFamilyPrices.push(products[i].familyId!);
                } else if(hasFamilyPrice && usedFamilyPrice && onlyUsedFamilyPrices) {
                    usedFamilyPriceIndexes.push(i);
                } else if(hasFamilyPrice && usedFamilyPrice && usedPromotionalPrice) {
                    beatenFamilyPrices.push(products[i].familyId!);
                    usedFamilyPriceIndexes.push(i);
                }
            }

            let everydayQuantity: null | number = null;

            for(const source in everydayUsed) {
                if(!Object.hasOwn(everydayUsed, source)) {
                    continue;
                }

                if(everydayQuantity === null) {
                    everydayQuantity = 0;
                }

                everydayQuantity += everydayUsed[source];
            }

            if(everydayQuantity !== null) {
                everydayQuantities[i] = everydayQuantity;
            }

            // Clean up
            ObjectPool.Release(possibilities);
            ObjectPool.Release(ks);
        }

        for(let i = 0, l = products.length; i < l; i++) {
            if(products[i].prices.used!.length === 0) {
                // Not valid, exit
                return false;
            }
        }

        if(!priceListOverride) {
            const promotionsToExclude = ArrayPool.Get<string>();

            for(const i in usedQuantities) {
                if(!Object.prototype.hasOwnProperty.call(usedQuantities, i)) {
                    continue;
                }

                if(typeof minimumQuantities[i] === "number") {
                    if(minimumQuantities[i] > usedQuantities[i]) {
                        promotionsToExclude.push(i);
                        excludedPromotions.push(i);
                    }
                }
            }

            ObjectPool.Release(usedQuantities);

            if(promotionsToExclude.length) {
                ArrayPool.Release(beatenFamilyPrices);
                ArrayPool.Release(usedFamilyPriceIndexes);

                const productsNoFamily: Array<ComparisonProduct> = ArrayPool.Get();
                const productsForPromotion: Array<ComparisonProduct> = ArrayPool.Get();
                let hasFamily = false;

                for(let i = 0, l = products.length; i < l; i++) {
                    const tempProduct = Cloner.Clone(products[i]);

                    productsForPromotion.push(Cloner.Clone(products[i]));

                    let canCompare = false;

                    for(let j = 0, n = tempProduct.prices.compare!.length; j < n; j++) {
                        if(excludedPromotions.includes(tempProduct.prices.compare![j].source)) {
                            canCompare = true;
                            break;
                        }
                    }

                    if(canCompare) {
                        for(let j = 0, n = tempProduct.prices.compare!.length; j < n; j++) {
                            if(tempProduct.prices.compare![j].source === "family") {
                                hasFamily = true;
                                tempProduct.prices.compare!.splice(j, 1);
                                break;
                            }
                        }
                    }

                    productsNoFamily.push(tempProduct);
                }

                let familyIsValid = false;

                if(hasFamily) {
                    familyIsValid = this.determineBestPrice(productsNoFamily, false, minimumQuantities);
                }

                const excludedPromotionsAreValid = this.determineBestPrice(
                    productsForPromotion,
                    priceListOverride,
                    minimumQuantities,
                    excludedPromotions,
                    alreadyCheckFamily
                );

                if(!familyIsValid && !excludedPromotionsAreValid) {
                    return false;
                }

                let useFamilyPrices = false;

                if(productsNoFamily.length) {
                    let noFamilyTotal = 0;
                    let promotionTotal = 0;

                    for(let i = 0, l = productsNoFamily.length; i < l; i++) {
                        noFamilyTotal += productsNoFamily[i].prices.price;
                    }

                    for(let i = 0, l = productsForPromotion.length; i < l; i++) {
                        promotionTotal += productsForPromotion[i].prices.price;
                    }

                    if(noFamilyTotal < promotionTotal) {
                        useFamilyPrices = true;
                    }
                }

                for(let i = 0, l = products.length; i < l; i++) {
                    if(useFamilyPrices) {
                        products[i].prices.price = productsNoFamily[i].prices.price;
                    } else {
                        products[i].prices.price = productsForPromotion[i].prices.price;
                    }
                }

                ArrayPool.Release(productsNoFamily);

                return true;
            }
        }

        if(beatenFamilyPrices.length) {
            const tempProducts: Array<ComparisonProduct> = ArrayPool.Get();
            const familyPrices = ObjectPool.Get() as Record<string, {
                old: number;
                new: number;
                oldPrices: Record<number, SalePromotionProduct["prices"]>;
                quantity: number;
            }>;

            for(let i = 0, n = usedFamilyPriceIndexes.length; i < n; i++) {
                const index = usedFamilyPriceIndexes[i];

                if(beatenFamilyPrices.includes(products[index].familyId!)) {
                    if(typeof familyPrices[products[index].familyId!] === "undefined") {
                        familyPrices[products[index].familyId!] = {
                            old      : 0,
                            new      : 0,
                            oldPrices: {},
                            quantity : 0,
                        };
                    }

                    familyPrices[products[index].familyId!].old += products[index].prices.price;
                    familyPrices[products[index].familyId!].oldPrices[index] = Cloner.Clone(products[index].prices);

                    for(let j = 0, l = products[index].prices.compare!.length; j < l; j++) {
                        if(products[index].prices.compare![j].source === "family") {
                            products[index].prices.compare!.splice(j, 1);
                            j--;
                            l--;
                        }
                    }

                    if(everydayQuantities[index] === 0) {
                        continue;
                    }

                    familyPrices[products[index].familyId!].quantity += everydayQuantities[index] ??
                        products[index].quantity;

                    tempProducts.push(products[index]);
                }
            }

            if(tempProducts.length) {
                this.updateAllFamiliesPrices(tempProducts);
                this.determineBestPrice(tempProducts, priceListOverride, minimumQuantities, [], true);
                // No need to do anything as the original products should be updated
                // due to implicitly passing by reference.

                for(let i = 0, l = tempProducts.length; i < l; i++) {
                    const prod = tempProducts[i];

                    if(!prod.familyId) {
                        continue; // Impossible however
                    }

                    familyPrices[prod.familyId].new += prod.prices.price;
                }

                for(const i in familyPrices) {
                    if(!Object.prototype.hasOwnProperty.call(familyPrices, i)) {
                        continue;
                    }

                    if(familyPrices[i].old < familyPrices[i].new) {
                        for(const k in familyPrices[i].oldPrices) {
                            if(!Object.prototype.hasOwnProperty.call(familyPrices[i].oldPrices, k)) {
                                continue;
                            }

                            products[parseInt(k)].prices = Cloner.Clone(familyPrices[i].oldPrices[k]);
                        }
                    }
                }
            }

            ArrayPool.Release(tempProducts);
            ObjectPool.Release(familyPrices);
        }

        ArrayPool.Release(beatenFamilyPrices);
        ArrayPool.Release(usedFamilyPriceIndexes);

        /*
        SHOPFRONT-4970 Redistribution now occurs in the comparison redistribution instead of here

        if(this.settings.rounding_mode === "redistribute") {
            const difference = scaleToDecimalPlaces(rounding, this.settings.decimal_places);

            if(difference !== 0) {
                products[applyRoundingTo].prices.price = scaleToDecimalPlaces(
                    products[applyRoundingTo].prices.price + difference,
                    this.settings.decimal_places
                );

                products[applyRoundingTo].prices.unlockedPrice = scaleToDecimalPlaces(
                    products[applyRoundingTo].prices.unlockedPrice + difference,
                    this.settings.decimal_places
                );

                products[applyRoundingTo].prices.normal = scaleToDecimalPlaces(
                    products[applyRoundingTo].prices.normal + difference,
                    this.settings.decimal_places
                );
            }
        }
        */

        return true;
    }

    /**
     * "Top Level" promotion calculation
     * @param products
     * @param promotion
     * @param useCrossPromotionCount
     * @param shouldCancel
     * @param applicablePromotions
     * @param comparisonProducts
     * @returns {Promise<*>}
     * @private
     */
    protected async calculatePromotion(
        products: Array<PromotionManagerProduct>,
        promotion: LocalDatabasePromotion,
        useCrossPromotionCount: boolean,
        shouldCancel: () => boolean,
        applicablePromotions: Array<string>,
        comparisonProducts: Array<ComparisonProduct>
    ): Promise<PromotionCalculationResult> {
        // Check if we can use the cache
        // At the moment don't use the cache as it isn't as tested as we would like
        /*
        if(!this.settings.cross_promotion_count && false) {
            // Attempt to get the promotion from the cache
            const uuids = ArrayPool.Get();
            uuids.push(promotion.uuid);

            const cached = await this._cache.get(uuids, products);

            if(cached !== undefined) {
                return cached.result;
            }
        }
         */

        if(!useCrossPromotionCount) {
            const res = AdvancedPromotionCache.getResult(promotion, products);

            if(res !== null) {
                return res;
            }
        }

        // Couldn't use the cache or the calculation doesn't exist in the cache, calculate
        const calculation = new PromotionCalculation(
            this,
            promotion,
            products,
            useCrossPromotionCount,
            shouldCancel,
            applicablePromotions,
            this.productCache,
            comparisonProducts
        );

        const res = await calculation.run();

        if(!useCrossPromotionCount) {
            AdvancedPromotionCache.storeResult(promotion, products, res);
        }

        return res;
    }

    /**
     * Apply promotional prices
     * @param products
     * @param promotion
     * @private
     */
    protected applyPromotionPrices(
        products: Array<any>,
        promotion: PromotionCalculationResultBranch | false
    ): Record<string, number> {
        if(promotion === false) {
            return ObjectPool.Get();
        }

        const minimums: Record<string, number> = {};

        for(let i = 0, l = promotion.minimumQuantity.length; i < l; i++) {
            if(typeof minimums[promotion.uuids[i]] === "undefined") {
                minimums[promotion.uuids[i]] = 0;
            }

            minimums[promotion.uuids[i]] += promotion.minimumQuantity[i];
        }

        if(promotion.products) {
            // Get the total promotion cost for the products
            const promotionCosts = ObjectPool.Get() as Record<string, {
                quantity: number;
                price: number;
                source: string;
            }>;

            for(let j = 0, n = promotion.products.length; j < n; j++) {
                if(typeof promotionCosts[promotion.products[j].uuid] === "undefined") {
                    promotionCosts[promotion.products[j].uuid] = ObjectPool.Get() as {
                        quantity: number;
                        price: number;
                        source: string;
                    };
                    promotionCosts[promotion.products[j].uuid].quantity = 0;
                    promotionCosts[promotion.products[j].uuid].price = 0;
                    promotionCosts[promotion.products[j].uuid].source = promotion.uuids.join(",");
                }

                promotionCosts[promotion.products[j].uuid].price += promotion.products[j].promotion as number;
                promotionCosts[promotion.products[j].uuid].quantity += promotion.products[j].quantity;
            }

            // Add the promotions to the comparisons
            for(let i = 0, l = products.length; i < l; i++) {
                if(typeof promotionCosts[products[i].uuid] === "undefined") continue;

                const rate = promotionCosts[products[i].uuid].price / promotionCosts[products[i].uuid].quantity;

                products[i].prices.compare.push({
                    quantity: 1,
                    price   : rate,
                    source  : promotionCosts[products[i].uuid].source,
                });
            }

            // Clean up
            ObjectPool.Release(promotionCosts);
        }

        return minimums;
    }

    /**
     * Apply the cross promotion prices to the products
     * @param products
     * @param solutions
     * @private
     */
    protected applyCrossPromotionPrices(products: Array<any>, solutions: Record<string, {
        price: number;
        quantity: number;
        used: Array<string>;
        everydayQuantity: number;
    }>): void {
        // Loop through the products and apply the solutions
        for(let i = 0, l = products.length; i < l; i++) {
            if(typeof solutions[products[i].uuid] === "undefined") continue;

            const rate = solutions[products[i].uuid].price / solutions[products[i].uuid].quantity;

            products[i].prices.compare.push({
                quantity: 1,
                price   : rate,
                source  : solutions[products[i].uuid].used.join(","),
                everyday: solutions[products[i].uuid].everydayQuantity,
            });
        }
    }

    /**
     * Compare the promotions to ensure the customer is getting the best promotion
     */
    protected comparePromotions(
        products: Array<ComparisonProduct>,
        calculated: Array<PromotionCalculationResult>,
        useCrossPromotionCount: boolean
    ): Record<string, number> {
        const minimums: Record<string, number> = ObjectPool.Get();

        // Loop through the calculated promotions
        if(!useCrossPromotionCount) {
            let promotion: false | PromotionCalculationResultBranch = false;

            for(let i = 0, l = calculated.length; i < l; i++) {
                const result = calculated[i] as false | Array<PromotionCalculationResultBranch>;

                if(result === false) continue;

                for(let j = 0, lj = result.length; j < lj; j++) {
                    if(result[j].total_percent) {
                        if((result[j].total_percent as number) % 1 !== 0) {
                            continue;
                        }
                    }

                    if(!promotion) {
                        promotion = result[j];
                        continue;
                    }

                    if(result[j].total_price < promotion.total_price) {
                        promotion = result[j];
                    }
                }
            }

            const mins = this.applyPromotionPrices(products, promotion);

            for(const i in mins) {
                if(!Object.prototype.hasOwnProperty.call(mins, i)) {
                    continue;
                }

                if(typeof minimums[i] === "undefined") {
                    minimums[i] = 0;
                }

                minimums[i] += mins[i];
            }

            ObjectPool.Release(mins);
        } else {
            const productComparisons = ObjectPool.Get() as Record<string, KnapsackObject>;

            const productsGrouped = groupProductsByUUID(products, false);
            const promotionQuantities = ObjectPool.Get() as Record<number, Record<number, number>>;

            for(let i = 0, l = calculated.length; i < l; i++) {
                const result = calculated[i] as false | PromotionCalculationResultSingle;

                if(result === false) continue;

                if(typeof minimums[result.uuid] === "undefined") {
                    minimums[result.uuid] = 0;
                }

                minimums[result.uuid] += result.minimumQuantity;

                if(typeof promotionQuantities[i] === "undefined") {
                    promotionQuantities[i] = ObjectPool.Get() as Record<number, number>;
                }

                for(let j = 0, n = result.products.length; j < n; j++) {
                    const product = result.products[j];

                    if(typeof product.compare === "undefined") continue;

                    if(typeof productComparisons[product.uuid] === "undefined") {
                        productComparisons[product.uuid] = Knapsack.getObject();
                    }

                    if(typeof promotionQuantities[i][j] === "undefined") {
                        promotionQuantities[i][j] = 0;
                    }

                    if(product.compare.quantity === 0) {
                        continue;
                    }

                    productComparisons[product.uuid].prices.push(
                        product.compare.price / product.compare.quantity
                    );
                    productComparisons[product.uuid].quantities.push(1);
                    productComparisons[product.uuid].maxQuantities.push(
                        product.compare.max ? product.compare.quantity : Infinity
                    );
                    productComparisons[product.uuid].identifiers.push(i + "|" + j);

                    promotionQuantities[i][j] += product.quantity;
                }
            }

            const solutions = ObjectPool.Get() as Record<string, {
                price: number;
                quantity: number;
                used: Array<string>;
                everydayQuantity: number;
            }>;

            for(const i in productComparisons) {
                if(!Object.prototype.hasOwnProperty.call(productComparisons, i)) continue;

                if(typeof productsGrouped[i] === "undefined") {
                    // Product has been discounted
                    continue;
                }

                // Add the everyday price to the calculation
                // This figure does not matter as long as it is higher than the promotions
                const everyday = productsGrouped[i].total_everyday_price;

                productComparisons[i].prices.push(everyday);
                productComparisons[i].quantities.push(1);
                productComparisons[i].maxQuantities.push(Infinity);
                productComparisons[i].identifiers.push("edp");

                const ks = Knapsack.solve(productComparisons[i], productsGrouped[i].total_quantity, true);

                if(!ks || !ks.used.length) {
                    // No promotion calculation is possible, just ignore
                    continue;
                }

                // let quantity          = 0; // NOTE: This returns odd quantities, perhaps work out why in the future
                const used = ArrayPool.Get<string>();
                let priceDifference = 0;
                let everydayQuantity = 0;

                for(let j = 0, n = ks.used.length; j < n; j++) {
                    if(ks.used[j] === "edp") {
                        priceDifference += everyday;
                        everydayQuantity += 1;
                        continue;
                    }

                    const id = ks.used[j].split("|");
                    const id0 = parseInt(id[0]);
                    // const id1 = parseInt(id[1]);

                    // quantity += promotionQuantities[id0][id1];
                    if(!used.includes((calculated[id0] as PromotionCalculationResultSingle).uuid)) {
                        used.push((calculated[id0] as PromotionCalculationResultSingle).uuid);
                    }
                }

                let current = 0;

                if(everydayQuantity !== 0) {
                    current = this.calculateEverydayPrice(i, everydayQuantity);
                }

                solutions[i] = ObjectPool.Get() as {
                    price: number;
                    quantity: number;
                    used: Array<string>;
                    everydayQuantity: number;
                };
                solutions[i].price = (ks.solution / ks.scale) - priceDifference + current;
                solutions[i].quantity = productsGrouped[i].total_quantity; // quantity
                solutions[i].used = used;
                solutions[i].everydayQuantity = everydayQuantity;
            }

            this.applyCrossPromotionPrices(products, solutions);
        }

        ArrayPool.Release(calculated);

        return minimums;
    }

    /**
     * Update each products promotional pricing
     */
    protected async updatePromotionalPricing(
        products: Array<ComparisonProduct>,
        promotions: Array<LocalDatabasePromotion>,
        forceCrossPromotionCount: boolean,
        localCalculationId: number,
        applicablePromotions: Array<string>
    ): Promise<Record<string, number>> {
        // Reformat the products
        const newProducts = ArrayPool.Get<PromotionManagerProduct>();
        const productIndexMap = ObjectPool.Get<Record<string, number>>();

        for(let i = 0, l = products.length; i < l; i++) {
            if(isSpecialProduct(products[i])) continue;

            // Check if product is a return
            if(products[i].quantity <= 0) continue;

            // NOTE: It is intentional that edited products continue through here.

            if(typeof productIndexMap[products[i].uuid] !== "undefined") {
                newProducts[productIndexMap[products[i].uuid]].quantity += products[i].quantity;
                newProducts[productIndexMap[products[i].uuid]].everyday += products[i].prices.normal;
                newProducts[productIndexMap[products[i].uuid]].base += products[i].prices.base * products[i].quantity;

                continue;
            }

            productIndexMap[products[i].uuid] = newProducts.length;
            newProducts.push({
                quantity     : products[i].quantity,
                everyday     : products[i].prices.normal,
                base         : products[i].prices.base * products[i].quantity,
                uuid         : products[i].uuid,
                case_quantity: products[i].case_quantity, // Used for comparison
                price_points : products[i].price_points!,
                special      : products[i].special,
                family       : products[i].familyId,
            });
        }

        // Determine if cross promotion count should be used
        const useCrossPromotionCount = this.settings.cross_promotion_count || forceCrossPromotionCount;

        // Loop through all the promotions and calculate them
        const promises = ArrayPool.Get<MaybePromise<PromotionCalculationResult>>();

        const startTime = Date.now();
        const maxTime = 400;
        let hasTimedOut = false;
        const shouldCancel = () => {
            if(!this.isTimeoutEnabled) {
                return false;
            }

            if(this.currentPromotionCalculation !== localCalculationId || hasTimedOut) {
                return true;
            }

            if(
                !useCrossPromotionCount &&
                Date.now() - startTime > maxTime
            ) {
                hasTimedOut = true;

                return true;
            }

            return false;
        };

        for(let i = 0, l = promotions.length; i < l; i++) {
            if(shouldCancel()) {
                break;
            }

            // Create a copy of the products
            const tempProducts = Cloner.Clone(newProducts);

            let promise: MaybePromise<PromotionCalculationResult> = this.calculatePromotion(
                tempProducts,
                promotions[i],
                useCrossPromotionCount,
                shouldCancel,
                applicablePromotions,
                products
            );

            if(
                !useCrossPromotionCount &&
                l > 10
            ) {
                promise = await promise;
            }

            promises.push(promise);
        }

        const calculated = await Promise.all(promises);

        // Release the required items
        ArrayPool.Release(newProducts);
        ArrayPool.Release(promises);

        if(hasTimedOut) {
            console.log("Promotion calculation timed out, forcing cross promotion count");
            ArrayPool.Release(calculated);

            return this.updatePromotionalPricing(
                products,
                promotions,
                true,
                localCalculationId,
                applicablePromotions
            );
        }

        // Calculate the promotions
        return this.comparePromotions(products, calculated, useCrossPromotionCount);
    }

    /**
     * Checks is a promotion is optional only
     * @param promotion
     * @returns {boolean}
     * @private
     */
    protected isOptionalOnly(promotion: LocalDatabasePromotion): boolean {
        if(this.settings.cross_promotion_count) return false;

        for(let i = 0, l = promotion.criteria.length; i < l; i++) {
            if(!promotion.criteria[i].optional) return false;
        }

        return true;
    }

    /**
     * Get the classifications currently in use on the sell screen
     * @protected
     */
    protected getClassifications(saleProducts: Array<SalePromotionProduct | PromotionManagerProduct>): {
        products: Array<string>;
        brands: Array<string>;
        categories: Array<string>;
        families: Array<string>;
        tags: Array<string>;
    } {
        if(typeof this.classificationCache === "undefined") {
            const products: Array<string> = [];
            const brands = [];
            const categories = [];
            const families = [];
            const tags = [];

            for(let i = 0, l = saleProducts.length; i < l; i++) {
                if(isSpecialProduct(saleProducts[i])) {
                    continue;
                }

                if(products.includes(saleProducts[i].uuid)) {
                    continue;
                }

                products.push(saleProducts[i].uuid);

                const dbProduct = this.productCache[saleProducts[i].uuid];

                if(dbProduct.brand_id) brands.push(dbProduct.brand_id);

                if(dbProduct.category_id) categories.push(dbProduct.category_id);

                if(dbProduct.family_id) families.push(dbProduct.family_id);

                tags.push(...dbProduct.tags);
            }

            this.classificationCache = {
                products,
                brands,
                categories,
                families,
                tags,
            };
        }

        return this.classificationCache;
    }

    /**
     * Check if a criteria is potentially in use by products on the sell screen.
     */
    protected isCriteriaInUse(
        criteria: LocalDatabasePromotionCriteria,
        products: Array<SalePromotionProduct | PromotionManagerProduct>
    ): boolean {
        const {
            products: prods,
            brands,
            categories,
            families,
            tags,
        } = this.getClassifications(products);

        for(let i = 0, l = criteria.products.length; i < l; i++) {
            if(prods.includes(criteria.products[i].uuid)) {
                return true;
            }
        }

        for(let i = 0, l = criteria.brands.length; i < l; i++) {
            if(brands.includes(criteria.brands[i].uuid)) {
                return true;
            }
        }

        for(let i = 0, l = criteria.categories.length; i < l; i++) {
            if(categories.includes(criteria.categories[i].uuid)) {
                return true;
            }
        }

        for(let i = 0, l = criteria.families.length; i < l; i++) {
            if(families.includes(criteria.families[i].uuid)) {
                return true;
            }
        }

        for(let i = 0, l = criteria.tags.length; i < l; i++) {
            if(tags.includes(criteria.tags[i].uuid)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Clean the promotions by removing duplicates and reformatting the raw data
     * @param dirtyPromotions
     * @param products
     * @returns {Array}
     * @private
     */
    protected cleanPromotions(
        dirtyPromotions: Array<Array<LocalDatabasePromotion>>,
        products: Array<SalePromotionProduct | PromotionManagerProduct>
    ): Array<LocalDatabasePromotion> {
        const promotions = ArrayPool.Get<LocalDatabasePromotion>();
        const cleanedPromotions = ArrayPool.Get<string>();

        // Dirty promotions contain an array for products and then promotions
        for(let i = 0, l = dirtyPromotions.length; i < l; i++) {
            for(let j = 0, n = dirtyPromotions[i].length; j < n; j++) {
                if(cleanedPromotions.includes(dirtyPromotions[i][j].uuid)) continue;

                // Check if optional only
                if(this.isOptionalOnly(dirtyPromotions[i][j])) {
                    cleanedPromotions.push(dirtyPromotions[i][j].uuid);

                    const criteria = dirtyPromotions[i][j].criteria;

                    // Make individual promotion for all optional criteria
                    for(let k = 0, c = criteria.length; k < c; k++) {
                        if(!(this.isCriteriaInUse(criteria[k], products))) {
                            continue;
                        }

                        const clonePromotion = Cloner.Clone(dirtyPromotions[i][j]);

                        // Previously this was `${uuid}|${index}` however, we use that to indicate shared promotion IDs
                        // where partial applications should be used, therefore, it's best to use a different unique
                        // separator for the ID of the promotion
                        clonePromotion.uuid += (":" + k);

                        const promoCriteria = Cloner.Clone(criteria[k]);

                        promoCriteria.optional = false;

                        clonePromotion.criteria = ArrayPool.Get();
                        clonePromotion.criteria.push(promoCriteria);

                        promotions.push(clonePromotion);
                    }
                } else {
                    promotions.push(dirtyPromotions[i][j]);
                    cleanedPromotions.push(dirtyPromotions[i][j].uuid);
                }
            }
        }

        // Release the dirty and clean promotions back into the pool
        ArrayPool.Release(cleanedPromotions);
        ArrayPool.Release(dirtyPromotions);

        return promotions;
    }

    /**
     * Check if a promotion is valid by checking the customer group, start date and end date
     * @param uuid
     * @param group
     * @param outlet
     * @param applicablePromotions
     * @returns {Promise<*>}
     * @private
     */
    protected async checkProductPromotionValid(
        uuid: string,
        group: string | null,
        outlet: string | null,
        applicablePromotions: Array<string>
    ): Promise<false | LocalDatabasePromotion> {
        let promotion;

        try {
            promotion = await this.promotionRepository.get(uuid);
        } catch {
            return false;
        }

        // Check if the promotion is active
        if(!promotion.active) {
            return false;
        }

        // Check if the current group is eligible to receive the promotion
        if(promotion.available_to && promotion.available_to.length > 0) {
            if(group === null || !promotion.available_to.includes(group)) {
                return false;
            }
        }

        // Check if the promotion is available to the current outlet
        if(outlet !== null && !promotion.outlets.includes(outlet)) {
            return false;
        }

        // Check if the promotion should be started
        if(promotion.start) {
            let start = moment(promotion.start, moment.ISO_8601, true);

            if(!start.isValid()) {
                start = moment(promotion.start, "Y-MM-DD HH:mm:ss");
            }

            if(start.isAfter(this.currentTime)) {
                // The promotion has not started yet
                return false;
            }
        }

        // Check if the promotion has already ended
        if(promotion.end) {
            let end = moment(promotion.end, moment.ISO_8601, true);

            if(!end.isValid()) {
                end = moment(promotion.end, "Y-MM-DD HH:mm:ss");
            }

            if(end.isBefore(this.currentTime)) {
                // The promotion has already finished
                return false;
            }
        }

        if(promotion.activated_by) {
            let isActivated = false;

            for(let i = 0, l = applicablePromotions.length; i < l; i++) {
                if(promotion.activated_by === applicablePromotions[i]) {
                    // In the future we may want to add some Regex matching to this check
                    isActivated = true;
                    break;
                }
            }

            if(!isActivated) {
                return false;
            }
        }

        // The promotion is valid, return it
        return promotion;
    }

    /**
     * Determine which promotions are available for the provided product
     * @param product
     * @param group
     * @param outlet
     * @param applicablePromotions
     * @returns {Promise<Array>}
     */
    public async determineProductPromotions(
        product: SalePromotionProduct | PromotionManagerProduct,
        group: string | null,
        outlet: string | null,
        applicablePromotions: Array<string>
    ): Promise<Array<LocalDatabasePromotion>> {
        // Check if product is a return
        if(product.quantity <= 0) {
            return ArrayPool.Get();
        }

        // Check if it's a special product
        if(isSpecialProduct(product)) {
            return ArrayPool.Get();
        }

        // Get the promotion from the database
        const dbPromotions = await this.promotionRepository.getPromotionsForProduct(product.uuid);

        // Remove the current promotions if required
        if(typeof product.promotions === "undefined") {
            ObjectPool.Release(product.promotions);
        }

        product.promotions = ObjectPool.Get();

        // Loop through the promotions applied to that product to check if they are valid
        const promotions = ArrayPool.Get<LocalDatabasePromotion>();

        for(let i = 0, l = dbPromotions.length; i < l; i++) {
            const promotion = await this.checkProductPromotionValid(
                dbPromotions[i],
                group,
                outlet,
                applicablePromotions
            );

            if(promotion) {
                promotions.push(promotion);
                product.promotions![promotion.uuid] = {
                    name    : promotion.name,
                    active  : false,
                    quantity: 0,
                };
            }

        }

        // Return the promotions
        return promotions;
    }

    /**
     * Determine what promotions are currently available
     * @param products
     * @param outlet
     * @param customer
     * @param applicablePromotions
     * @returns {Promise<Array>}
     * @private
     */
    public async determineAvailablePromotions(
        products: Array<SalePromotionProduct | PromotionManagerProduct>,
        outlet: string | null,
        customer: LocalDatabaseCustomer | null = null,
        applicablePromotions: Array<string> = []
    ): Promise<Array<LocalDatabasePromotion>> {
        // Check if the customer receives promotions
        if(customer && customer.disable_promotions) {
            // The customer does not receive promotions
            return [];
        }

        // Determine which group we should be getting promotions for
        let group = null;

        if(customer && customer.customer_group_id) {
            group = customer.customer_group_id;
        }

        // Loop through the products
        const promises = ArrayPool.Get<Promise<Array<LocalDatabasePromotion>>>();

        for(let i = 0, l = products.length; i < l; i++) {
            // Determine the promotions
            promises.push(this.determineProductPromotions(products[i], group, outlet, applicablePromotions));
        }

        // Resolve the promises
        const productPromotions = await Promise.all(promises);

        // Release the promises back into the pool
        ArrayPool.Release(promises);

        // Clean up and return the promotions
        return this.cleanPromotions(productPromotions, products);
    }

    /**
     * Get the loyalty rates for the specified product
     */
    protected async getLoyaltyRates(uuid: string, quantity: number): Promise<{ earn: number; redeem: number; }> {
        // Get the product from the database
        if(typeof this.productCache[uuid] === "undefined") {
            try {
                this.productCache[uuid] = await this.productRepository.get(uuid);
            } catch(e) {
                if(e instanceof NotFoundException) {
                    return {
                        earn  : 0,
                        redeem: 0,
                    };
                }

                throw e;
            }
        }

        const product = this.productCache[uuid];

        // Allowing to override earn / redeem rate
        let overrideEarn = true;
        let overrideRedeem = true;

        // Setup the default rates
        const rates = ObjectPool.Get() as {
            earn: number;
            redeem: number;
        };

        rates.earn = this.settings.earn_rate;
        rates.redeem = this.settings.redemption_rate;

        // Check if the product has loyalty rates set
        if(product.loyalty_rates.length) {
            // Loop through the loyalty rates until we get to the closest quantity
            const priceRates = ObjectPool.Get() as {
                quantity: number;
                earn: number | null;
                redeem: number | null;
            };

            priceRates.quantity = 0;
            priceRates.earn = 0;
            priceRates.redeem = 0;

            for(let i = 0, l = product.loyalty_rates.length; i < l; i++) {
                if(
                    product.loyalty_rates[i].quantity > priceRates.quantity &&
                    product.loyalty_rates[i].quantity <= quantity
                ) {
                    priceRates.quantity = product.loyalty_rates[i].quantity;
                    priceRates.earn = product.loyalty_rates[i].loyalty_value;
                    priceRates.redeem = product.loyalty_rates[i].redeem_value;
                }
            }

            if(priceRates.quantity !== 0) {
                if(priceRates.earn !== null) {
                    rates.earn = priceRates.earn;
                    overrideEarn = false;
                }

                if(priceRates.redeem !== null) {
                    rates.redeem = priceRates.redeem;
                    overrideRedeem = false;
                }

                // This overrides all other settings
                if(priceRates.earn !== null && priceRates.redeem !== null) {
                    ObjectPool.Release(priceRates);

                    return rates;
                }
            }

            ObjectPool.Release(priceRates);
        }

        const comparisons = ArrayPool.Get<LocalDatabaseLoyalty>();

        if(product.category_id) {
            const category = await this.loyaltyRepository.getByType(product.category_id, "category")
                .catch(() => false as const);

            if(category) {
                comparisons.push(...category);
            }
        }

        if(product.brand_id) {
            const brand = await this.loyaltyRepository.getByType(product.brand_id, "brand")
                .catch(() => false as const);

            if(brand) {
                comparisons.push(...brand);
            }
        }

        if(product.family_id) {
            const family = await this.loyaltyRepository.getByType(product.family_id, "family")
                .catch(() => false as const);

            if(family) {
                comparisons.push(...family);
            }
        }

        for(let j = 0, n = product.tags.length; j < n; j++) {
            const tag = await this.loyaltyRepository.getByType(product.tags[j], "tag")
                .catch(() => false as const);

            if(tag) {
                comparisons.push(...tag);
            }
        }

        // Check the product's comparisons to determine the loyalty rate
        const priceRates = ObjectPool.Get() as {
            quantity: number;
            earn: number | null;
            redeem: number | null;
        };

        priceRates.quantity = 0;
        priceRates.earn = 0;
        priceRates.redeem = 0;

        for(let i = 0, l = comparisons.length; i < l; i++) {
            const comparisonQuantity = parseFloat(comparisons[i].quantity);

            if(comparisonQuantity > priceRates.quantity && comparisonQuantity <= quantity) {
                priceRates.quantity = comparisonQuantity;
                priceRates.earn = comparisons[i].loyalty_value === null ?
                    null :
                    parseFloat(comparisons[i].loyalty_value as string);
                priceRates.redeem = comparisons[i].redeem_value === null ?
                    null :
                    parseFloat(comparisons[i].redeem_value as string);
            }
        }

        // Compare the price rate to the earn and redeem rates
        if(priceRates.quantity !== 0) {
            if(overrideEarn && priceRates.earn === 0) {
                rates.earn = 0;
            } else if(overrideEarn && priceRates.earn !== null && priceRates.earn > rates.earn) {
                rates.earn = priceRates.earn;
            }

            if(overrideRedeem && priceRates.redeem === 0) {
                rates.redeem = 0;
            } else if(overrideRedeem && priceRates.redeem !== null && priceRates.redeem > rates.redeem) {
                rates.redeem = priceRates.redeem;
            }
        }

        ObjectPool.Release(priceRates);
        ArrayPool.Release(comparisons);

        // Return the rates
        return rates;
    }

    /**
     * Calculate the loyalty amounts
     * @param rates
     * @param quantity
     * @param price
     * @returns {{earn: {base: number|*, total: number}, redeem: {base: number|*, total: number}}}
     * @private
     */
    protected calculateLoyaltyAmount(
        rates: { earn: number; redeem: number; },
        quantity: number,
        price: number
    ): {
        earn: {
            base: number;
            total: number;
        };
        redeem: {
            base: number;
            total: number;
        };
    } {
        const loyalty = ObjectPool.Get() as ReturnType<typeof PromotionManager.prototype.calculateLoyaltyAmount>;

        loyalty.earn = ObjectPool.Get() as { base: number; total: number; };
        loyalty.earn.base = rates.earn;
        loyalty.earn.total = 0;
        loyalty.redeem = ObjectPool.Get() as { base: number; total: number; };
        loyalty.redeem.base = rates.redeem;
        loyalty.redeem.total = 0;

        // Calculate the total loyalty
        loyalty.earn.total += rates.earn * price;
        loyalty.redeem.total += rates.redeem * price;

        return loyalty;
    }

    /**
     * Calculate the loyalty for a single product
     */
    protected async calculateLoyaltyForProduct(
        products: Array<SalePromotionProduct | SaleProduct>,
        uuid: string,
        details: GroupedProduct
    ): Promise<void> {
        // Get the loyalty rates for the product
        const rates = await this.getLoyaltyRates(uuid, details.total_quantity);

        // Calculate the loyalty
        const loyalty = this.calculateLoyaltyAmount(rates, details.total_quantity, details.total_price);

        // Distribute the loyalty
        const loyaltyPerDollar = ObjectPool.Get() as { earn: number; redeem: number; };

        loyaltyPerDollar.earn = loyalty.earn.total / details.total_price;
        loyaltyPerDollar.redeem = loyalty.redeem.total / details.total_price;

        for(let i = 0, l = products.length; i < l; i++) {
            if(products[i].uuid !== uuid) continue;

            products[i].loyalty = {
                earn  : loyaltyPerDollar.earn * products[i].prices.price,
                redeem: loyaltyPerDollar.redeem * products[i].prices.price,
            };
        }

        // Clean up
        ObjectPool.Release(rates);
        ObjectPool.Release(loyalty);
        ObjectPool.Release(loyaltyPerDollar);
    }

    /**
     * Calculate the loyalty for all products
     */
    public calculateLoyalty(
        products: Array<SaleProduct | SalePromotionProduct>,
        customer: LocalDatabaseCustomer | null = null
    ): Promise<Array<void>> {
        // Check to see if the customer can have loyalty
        if(customer && !customer.loyalty) {
            for(let i = 0, l = products.length; i < l; i++) {
                products[i].loyalty = {
                    earn  : 0,
                    redeem: 0,
                };
            }

            return Promise.resolve(ArrayPool.Get());
        }

        // Group the products
        const prods = groupProductsByUUID(products, false, false, true);

        // Get the loyalty rates for the product
        const loyaltyPromises = ArrayPool.Get<Promise<void>>();

        for(const i in prods) {
            if(!Object.prototype.hasOwnProperty.call(prods, i)) continue;

            loyaltyPromises.push(this.calculateLoyaltyForProduct(products, i, prods[i]));
        }

        // Resolve and return all promises
        return Promise.all(loyaltyPromises).then((resolved) => {
            // Release the products
            ObjectPool.Release(prods);

            // Ensure we return the resolved data
            return resolved;
        });
    }

    /**
     * Calculate the rebates for the products
     * @param products
     * @param promotions
     */
    protected calculateRebates(
        products: Array<SalePromotionProduct>,
        promotions: Array<LocalDatabasePromotion>
    ): Promise<void> {
        return new RebateCalculationManager(this, products, promotions).run();
    }

    protected calculateComponentPrice(
        products: Array<SalePromotionProduct | SaleProduct>,
        defaultProducts: Array<SaleProduct>
    ): [ base: number, total: number ] {
        let price = 0;
        let basePrice = 0;

        for(let i = 0, l = products.length; i < l; i++) {
            let adjustment = 0;
            let base = 0;

            if(products[i].deleted) {
                if(products[i].initiallyDeleted || typeof defaultProducts[i] !== "undefined") {
                    // Check the remove price
                    if(products[i].edited) {
                        adjustment = products[i].prices.price;
                    } else {
                        adjustment = products[i].prices.removePrice;
                    }

                    base = products[i].prices.removePrice;
                }
            } else {
                // Add the children of this item
                [ base, adjustment ] = this.calculateComponentPrice(products[i].products, products[i].defaultProducts);

                // Add this item
                if(products[i].edited) {
                    adjustment = products[i].prices.price;
                } else {
                    adjustment += products[i].prices.addPrice;
                }

                base += products[i].prices.addPrice;
            }

            products[i].prices.price = adjustment;

            if(!products[i].edited) {
                products[i].prices.unlockedPrice = adjustment;
            }

            price += adjustment;
            basePrice += base;
        }

        return [ basePrice, price ];
    }

    /**
     * Add the prices for each component
     * @param products
     * @protected
     */
    protected addComponentPrices(products: Array<SalePromotionProduct>): void {
        for(let i = 0, l = products.length; i < l; i++) {
            // Previously we were subtracting and adding back in the previous price, but the removal now happens in the
            // comparison product distribution.

            if(products[i].edited) {
                // If the product isn't edited, the prices are calculated using only the parent item, so they need to
                // be fully re-added back on
                products[i].prices.unlockedPrice -= products[i].prices.additionalPrice[0];

                if(typeof products[i].prices.discountRate === "undefined") {
                    // The discount rate overrides this already to adjust the parent item to the value without the
                    // additional price
                    products[i].prices.price -= products[i].prices.additionalPrice[1];
                }
            }

            this.addComponentPrices(products[i].products as Array<SalePromotionProduct>);

            const [ base, total ] = this.calculateComponentPrice(
                products[i].products,
                products[i].defaultProducts
            );

            products[i].prices.additionalPrice = [ base * products[i].quantity, total * products[i].quantity ];

            products[i].prices.unlockedPrice += products[i].prices.additionalPrice[0];
            products[i].prices.price += products[i].prices.additionalPrice[1];
        }
    }

    /**
     * Apply the discount rates to each product
     * @param products
     * @protected
     */
    protected applyProductDiscounts(products: Array<Override<SaleProduct, {
        case_quantity: number;
        is_case: boolean;
        caseQuantity?: number;
        isCase?: boolean;
        sortKey?: number;
    }> | SaleProduct>): void {
        for(let i = 0, l = products.length; i < l; i++) {
            this.applyProductDiscounts(products[i].products);

            const discountRate = products[i].prices.discountRate;

            if(typeof discountRate !== "undefined") {
                products[i].prices.price = discountRate * products[i].quantity;
            }
        }
    }

    /**
     * Get the current prices for the product
     */
    public static determineCurrentPrices(
        product: LocalDatabaseProduct,
        priceSet: string | false,
        priceSets: Record<string, string | null>
    ): Array<SalePromotionProductPrice> {
        // In the future, it would be good to remove the clone
        const dbPrices = Cloner.Clone(product.prices);

        // Determine which price set we are using
        const prices: Array<SalePromotionProductPrice> = [];

        // Look for the prices within the price set
        let depth = 0;

        while(priceSet !== false) {
            if(depth > 16) {
                console.warn("Ignoring price set as the depth is greater than 16");

                break;
            }

            for(let i = 0, l = dbPrices.length; i < l; i++) {
                if(dbPrices[i].price_set_id === priceSet || (!priceSet && !dbPrices[i].price_set_id)) {
                    prices.push(dbPrices[i]);
                }
            }

            if(prices.length) {
                break;
            }

            priceSet = priceSets[priceSet] ?? false;
            depth++;
        }

        // If no price exists, use the default prices
        if(!prices.length) {
            for(let i = 0, l = dbPrices.length; i < l; i++) {
                if(dbPrices[i].price_set_id === "" || !dbPrices[i].price_set_id) {
                    prices.push(dbPrices[i]);
                }
            }
        }

        // Return the prices
        return prices;
    }

    /**
     * Get all the prices possible for the product to calculate the everyday price
     */
    protected getEverydayPrices(
        product: { uuid: string; price_points?: Array<SalePromotionProductPrice>; },
        throwOnMissingProduct: boolean,
        overridePriceSetId?: string
    ): Array<SalePromotionProductPrice> {
        // Get the database product
        const dbProduct = this.productCache[product.uuid];

        if(typeof dbProduct === "undefined") {
            if(throwOnMissingProduct) {
                throw new Error(`Product ${product.uuid} does not exist in the cache.`);
            }

            // Item has been removed out of the cache, technically there's a race condition here, but we know we're not
            // in the correct promotion context anymore.
            return [];
        }

        // Check if the product's price points are set
        product.price_points = []; // Don't use the pool

        return PromotionManager.determineCurrentPrices(
            dbProduct,
            overridePriceSetId ?? this.settings.price_set,
            this.priceSetCache.parents
        );
    }

    /**
     * Calculate the price using quantity rate
     * @param prices
     * @param quantity
     * @returns {number}
     */
    public static calculateQuantityRate(prices: Array<SalePromotionProductPrice>, quantity: number): number {
        let currentRate = prices[0].price / prices[0].quantity;

        for(let j = 1, m = prices.length; j < m; j++) {
            if(prices[j].quantity <= quantity) {
                const newRate = prices[j].price / prices[j].quantity;

                if(newRate < currentRate) {
                    currentRate = newRate;
                }
            } else {
                break;
            }
        }

        return currentRate * quantity;
    }

    /**
     * Calculate the price using high mix
     * @param prices
     * @param quantity
     * @returns {number}
     */
    public static calculateHighMixPrice(prices: Array<SalePromotionProductPrice>, quantity: number): number {
        let currentPrice = 0;

        if(!prices.length) {
            return currentPrice;
        }

        // Use the knapsack solution to calculate the price
        const possibilities = Knapsack.getObject<number>();

        // Configure the parameters
        let i, l;
        let hasSinglePrice = false;

        for(i = 0, l = prices.length; i < l; i++) {
            if(prices[i].price === undefined) continue;

            possibilities.prices.push(prices[i].price);
            possibilities.quantities.push(prices[i].quantity);
            possibilities.identifiers.push(i);
            possibilities.maxQuantities.push(Infinity);

            if(prices[i].quantity === 1) {
                hasSinglePrice = true;
            }
        }

        if(!hasSinglePrice) {
            const rate = prices[0].price / prices[0].quantity;

            prices.push({
                quantity: 1,
                price   : rate,
            });

            possibilities.prices.push(rate);
            possibilities.quantities.push(1);
            possibilities.identifiers.push(prices.length - 1);
            possibilities.maxQuantities.push(Infinity);
        }

        // Calculate the price
        let ks = Knapsack.solve(possibilities, quantity, true);

        if(typeof ks.solution !== "undefined" && ks.solution !== undefined) {
            currentPrice = scaleToDecimalPlaces(ks.solution / ks.scale, 5);
        } else {
            ks = Knapsack.solve(possibilities, Math.round(quantity), true);

            const difference = quantity - Math.round(quantity);

            for(i = 0, l = ks.used.length; i < l; i++) {
                currentPrice = Math.round((currentPrice * 10000) + (prices[ks.used[i]].price * 10000)) / 10000;
            }

            currentPrice += prices[0].price * difference;
        }

        if(ks.usedQuantity !== quantity && prices.length) {
            const quantityDiff = quantity - ks.usedQuantity;

            currentPrice += prices[0].price / prices[0].quantity * quantityDiff;
        }

        // Release the knapsack possibilities
        ObjectPool.Release(possibilities);

        // Return the current price
        return currentPrice;
    }

    /**
     * Get the everyday price for the provided product
     */
    public async everydayPrice(product: string, quantity: number, overridePriceSetId?: string): Promise<number> {
        if(typeof this.productCache[product] === "undefined") {
            try {
                this.productCache[product] = await this.productRepository.get(product);
            } catch(e) {
                if(e instanceof NotFoundException) {
                    return 0;
                }
            }
        }

        if(!this.priceSetCache.valid) {
            const priceSets = await this.priceSetRepository.all();
            const priceSetCache = {} as Record<string, string | null>;

            for(const priceSet of priceSets) {
                priceSetCache[priceSet.uuid] = priceSet.parent_id ?? null;
            }

            this.priceSetCache.valid = true;
            this.priceSetCache.parents = priceSetCache;
        }

        return this.calculateEverydayPrice(product, quantity, true, overridePriceSetId);
    }

    /**
     * Determine the everyday price. To use this, please ensure the product is already in the cache, if you're uncertain
     * use `everydayPrice` instead
     */
    public calculateEverydayPrice(
        product: string | {
            uuid: string;
            price_points?: Array<SalePromotionProductPrice>;
        } | {
            uuid: string;
            special: boolean;
            prices: {
                price: number;
            };
            price_points?: Array<SalePromotionProductPrice>;
        },
        quantity: number,
        throwOnMissingProduct = true,
        overridePriceSetId?: string
    ): number {
        if(quantity === 0) {
            return 0;
        }

        let associatePrices = true;

        // Check if the product is an actual product
        if(typeof product === "string") {
            associatePrices = false;

            product = {
                uuid: product,
            };
        }

        // Check if we are looking at an edited product
        if("special" in product && isSpecialProduct(product)) {
            return product.prices.price;
        }

        const cacheId = `${product.uuid}-${quantity}`;

        // Check if we've already calculated the price
        if(typeof this.priceCache[cacheId] !== "undefined") {
            if(associatePrices) {
                product.price_points = this.priceCache[cacheId].pricePoints;
            }

            return this.priceCache[cacheId].price;
        }

        // Check if it's a negative quantity
        let multiplier = 1;

        if(quantity < 0) {
            multiplier = -1;
            quantity *= -1;
        }

        // Get the prices for the product
        const prices = this.getEverydayPrices(product, throwOnMissingProduct, overridePriceSetId);

        if(!prices.length) {
            this.priceCache[cacheId] = {
                price      : 0,
                pricePoints: prices,
            };

            if(associatePrices) {
                product.price_points = prices;
            }

            return 0;
        }

        // Determine the everyday price
        let price;

        if(this.settings.use_quantity_rate) {
            price = PromotionManager.calculateQuantityRate(prices, quantity) * multiplier;
        } else {
            price = PromotionManager.calculateHighMixPrice(prices, quantity) * multiplier;
        }

        this.priceCache[cacheId] = {
            price,
            pricePoints: prices,
        };

        if(associatePrices) {
            product.price_points = prices;
        }

        return price;
    }

    /**
     * Update the everyday price for a single product and add the price to the comparison array
     */
    protected updateEverydayPrice(product: SalePromotionProduct): void {
        // Check if we already have a temporary price array on the product
        product.prices.compare = []; // Don't use the pool

        // Get the everyday price
        const price = this.calculateEverydayPrice(product, product.quantity, false);

        // Push the everyday price to the comparison array
        product.prices.compare.push({
            quantity: 1,
            price   : price / Math.abs(product.quantity),
            source  : "everyday",
        });

        if(!product.special) {
            // Update the everyday price
            product.prices.normal = price;
            product.prices.base = this.calculateEverydayPrice(product, 1, false);
        }
    }

    /**
     * Distribute the prices over a group of products (either family or individual products across multiple lines)
     */
    protected distributeGroupPrices(
        group: {
            price: number;
            quantity: number;
            isNegative: boolean;
            products: Array<number>;
        },
        products: Array<ComparisonProduct>,
        priceRatio: number,
        sourceName: string,
        neverRedistribute: boolean,
        idealDistribution: Record<string, Record<number, number>> = {}
    ): void {
        // Before we start distributing based on the Vendor's rules, we need to handle the idealDistribution variable;
        // this is keyed by product -> quantity -> price. Essentially, it should work by checking if the quantity is
        // divisible directly to the product and then using the associated price before distributing the rest of the
        // items.
        // This shouldn't matter which distribution method is used.
        if(!group.isNegative) {
            const appliedIndexes = [];

            for(const id in idealDistribution) {
                if(!Object.hasOwn(idealDistribution, id)) continue;

                let applied = -1;

                // Find the product the distribution is for
                for(const index of group.products) {
                    if(products[index].uuid !== id) {
                        continue;
                    }

                    // Check if we match any of the distribution quantities
                    for(const quantityString in idealDistribution[id]) {
                        if(!Object.hasOwn(idealDistribution[id], quantityString)) continue;

                        const quantity = parseInt(quantityString);

                        if(isNaN(quantity)) {
                            continue;
                        }

                        if(products[index].quantity % quantity !== 0) {
                            continue;
                        }

                        // Quantity matches, we need to apply the price and remove the product from the group
                        const applyCount = products[index].quantity / quantity;

                        group.price -= idealDistribution[id][quantityString] * applyCount;
                        group.quantity -= products[index].quantity;

                        if(typeof products[index].prices.compare === "undefined") {
                            products[index].prices.compare = [];
                        }

                        products[index].prices.compare!.push({
                            source  : sourceName,
                            price   : idealDistribution[id][quantityString] * applyCount / products[index].quantity,
                            quantity: 1,
                        });

                        applied = index;
                        break;
                    }

                    if(applied !== -1) {
                        appliedIndexes.push(applied);

                        break;
                    }
                }
            }

            if(appliedIndexes.length) {
                const newProductIndexes = [];

                for(const index of group.products) {
                    if(appliedIndexes.includes(index)) continue;

                    newProductIndexes.push(index);
                }

                group.products = newProductIndexes;
            }

            if(group.products.length === 0 && group.quantity === 0 && group.price === 0) {
                return;
            } else if(group.products.length === 0 && (group.quantity !== 0 || group.price !== 0)) {
                // Need to reverse the distribution as there wasn't an ideal distribution
                for(const index of appliedIndexes) {
                    let distributionPrice = null;
                    let distributionIndex = -1;

                    // The distribution is most likely to be the last item, so go through the list in reverse
                    for(let i = products[index].prices.compare!.length - 1; i >= 0; i--) {
                        if(products[index].prices.compare![i].source === sourceName) {
                            distributionPrice = products[index].prices.compare![i].price;
                            distributionIndex = i;

                            break;
                        }
                    }

                    if(distributionPrice === null) {
                        continue;
                    }

                    group.quantity += products[index].quantity;
                    group.price += distributionPrice * products[index].quantity;
                    group.products.push(index);
                    products[index].prices.compare!.splice(distributionIndex, 1);
                }
            }
        }

        // Get the current group quantity
        const quantity = group.quantity;

        // Determine the distribution method
        let shouldMatchPricePoints = this.settings.family_distribution === "match_price_points";

        if(shouldMatchPricePoints) {
            // Check if there are multiple price points
            let hasMultiplePricePoints = false;
            let pricePoints: false | Array<SalePromotionProductPrice> = false;

            for(let i = 0, l = group.products.length; i < l; i++) {
                const product = products[group.products[i]];

                if(pricePoints === false) {
                    pricePoints = product.price_points ?? false;
                    continue;
                }

                if(pricePoints.length !== product.price_points!.length) {
                    hasMultiplePricePoints = true;
                    break;
                }

                for(let j = 0, n = pricePoints.length; j < n; j++) {
                    if(pricePoints[j].quantity !== product.price_points![j].quantity) {
                        hasMultiplePricePoints = true;
                        break;
                    } else if(pricePoints[j].price !== product.price_points![j].price) {
                        hasMultiplePricePoints = true;
                        break;
                    }
                }

                if(hasMultiplePricePoints) {
                    break;
                }
            }

            // Check if we are adding up to an existing price point
            if(hasMultiplePricePoints || pricePoints === false || !pricePoints.length) {
                shouldMatchPricePoints = false;
            } else {
                let matchingQuantity = false;

                for(let i = 0, l = pricePoints.length; i < l; i++) {
                    if(pricePoints[i].quantity === quantity) {
                        matchingQuantity = true;
                        break;
                    }
                }

                if(matchingQuantity) {
                    shouldMatchPricePoints = false;
                }
            }
        }

        // Distribute the price
        if(shouldMatchPricePoints) {
            // =========================================
            // Distribute by matching to price points
            // =========================================
            let remainingFamilyPrice = group.price;
            let remainingFamilyQuantity = group.quantity;
            const allocatedProducts = [];

            // Step 1: Set the existing price points
            for(let i = 0, l = group.products.length; i < l; i++) {
                // Check if this product matches a price point
                let matchesPricePoint = false;
                let index = -1;
                const productQuantity = Math.abs(products[group.products[i]].quantity);

                for(let j = 0, n = products[group.products[i]].price_points!.length; j < n; j++) {
                    const priceQuantity = products[group.products[i]].price_points![j].quantity;

                    if(productQuantity === 1 && priceQuantity === 1) {
                        matchesPricePoint = true;
                        index = j;
                        break;
                    } else if(priceQuantity === 1) {
                        // Don't use a multiple of one as a check (as it matches to most realistically used quantities)
                        // noinspection UnnecessaryContinueJS
                        continue;
                    } else if(productQuantity % priceQuantity === 0) {
                        matchesPricePoint = true;
                        index = j;
                    }
                }

                if(matchesPricePoint) {
                    // The products' quantity matches a price point, use it
                    const applyMultiplier =
                        productQuantity / products[group.products[i]].price_points![index].quantity;
                    let price = applyMultiplier * products[group.products[i]].price_points![index].price;

                    if(group.isNegative) {
                        price = price * -1;
                    }

                    // Update the everyday price
                    products[group.products[i]].prices.normal = price;

                    // Add to the comparison
                    products[group.products[i]].prices.compare!.push({
                        quantity: 1,
                        price:
                            (price / applyMultiplier) / products[group.products[i]].price_points![index].quantity,
                        compare: priceRatio,
                        source : sourceName,
                    });

                    // Add to the matching products
                    allocatedProducts.push(i);

                    // Remove the price and quantity from the family
                    remainingFamilyPrice -= price;
                    remainingFamilyQuantity -= productQuantity;
                } else {
                    // This product's quantity doesn't match
                    // Do nothing
                }

                // Remove the everyday comparison
                const newComparison = [];

                for(let j = 0, n = products[group.products[i]].prices.compare!.length; j < n; j++) {
                    if(products[group.products[i]].prices.compare![j].source !== "everyday") {
                        newComparison.push(products[group.products[i]].prices.compare![j]);
                    }
                }

                products[group.products[i]].prices.compare = newComparison;
            }

            // Step 2: Distribute the remaining price to the remaining products
            if(remainingFamilyPrice !== 0 || remainingFamilyQuantity !== 0) {
                const priceRatio = remainingFamilyPrice / remainingFamilyQuantity;

                for(let i = 0, l = group.products.length; i < l; i++) {
                    if(allocatedProducts.includes(i)) {
                        continue;
                    }

                    // Get the distributed price
                    let price;

                    if(group.products.length === 1) {
                        price = priceRatio * products[group.products[i]].quantity;
                    } else {
                        price = Math.round(priceRatio * products[group.products[i]].quantity * 100) / 100;
                    }

                    // Update the everyday price
                    products[group.products[i]].prices.normal = price;

                    // Add to the comparison
                    products[group.products[i]].prices.compare!.push({
                        quantity: 1,
                        price   : price / products[group.products[i]].quantity * (
                            products[group.products[i]].quantity < 0 ?
                                -1 :
                                1
                        ),
                        compare: priceRatio,
                        source : sourceName,
                    });

                    // Remove from the remaining price
                    remainingFamilyPrice -= price;
                }
            }

            // Step 3: Adjust the rounding
            remainingFamilyPrice = Math.round(remainingFamilyPrice * 10000) / 10000;

            if(remainingFamilyPrice !== 0 && !neverRedistribute) {
                const index = group.products[group.products.length - 1];

                // Get the price
                const price = (products[index].prices.normal + remainingFamilyPrice) / products[index].quantity;

                // Adjust the everyday price
                products[index].prices.normal = price * products[index].quantity;

                // Adjust the comparison
                products[index].prices.compare![products[index].prices.compare!.length - 1].price =
                    price * (products[index].quantity < 0 ? -1 : 1);
            }
        } else {
            // =========================================
            // Distribute proportionally
            // =========================================
            const familyPrice = group.price;
            const distribution = familyPrice / quantity;
            let runningTotal = 0;
            const negative = quantity < 0 ? -1 : 1;

            for(let i = 0, l = group.products.length; i < l; i++) {
                // Get the price
                const price = Math.round(
                    distribution * products[group.products[i]].quantity * 100
                ) / 100;

                // Adjust the everyday price
                products[group.products[i]].prices.normal = price;

                // Add to the comparisons
                products[group.products[i]].prices.compare!.push({
                    quantity: 1,
                    price   : price / products[group.products[i]].quantity * negative,
                    source  : sourceName,
                });

                // Add to the running total
                runningTotal += price;

                // Remove the everyday comparison
                const newComparison = [];

                for(let j = 0, n = products[group.products[i]].prices.compare!.length; j < n; j++) {
                    if(products[group.products[i]].prices.compare![j].source !== "everyday") {
                        newComparison.push(products[group.products[i]].prices.compare![j]);
                    }
                }

                products[group.products[i]].prices.compare = newComparison;
            }

            const remainingFamilyPrice = Math.round((runningTotal - familyPrice) * 10000) / 10000;

            if(remainingFamilyPrice !== 0 && !neverRedistribute && this.settings.rounding_mode === "redistribute") {
                // Adjust rounding
                const index = group.products[group.products.length - 1];

                // Get the price
                const price = (
                    products[index].prices.normal - remainingFamilyPrice
                ) / products[index].quantity * negative;

                // Adjust the everyday price
                products[index].prices.normal = price * products[index].quantity;

                // Adjust the comparison
                products[index].prices.compare![products[index].prices.compare!.length - 1].price = price;
            }
        }
    }

    protected updateFamilyPrice(
        family: {
            price: number;
            quantity: number;
            products: Array<number>;
            isNegative: boolean;
        },
        products: Array<ComparisonProduct>,
        neverRedistribute: boolean
    ): void {
        // Get the current family quantity
        const quantity = family.quantity;

        // Get the price for the family
        for(let i = 0, l = family.products.length; i < l; i++) {
            const price = this.calculateEverydayPrice(products[family.products[i]].uuid, quantity);

            family.price += price / quantity * products[family.products[i]].quantity;
        }

        // Get the ratio (what to actually compare when comparing prices)
        const familyPriceRatio = family.price / quantity;

        // Distribute the prices
        this.distributeGroupPrices(family, products, familyPriceRatio, "family", neverRedistribute);
    }

    /**
     * Update the price for all families
     */
    public updateAllFamiliesPrices(products: Array<ComparisonProduct>, neverRedistribute = false): void {
        // Determine the families
        const families = ObjectPool.Get() as Record<string, {
            quantity: number;
            price: number;
            products: Array<number>;
            isNegative: boolean;
        }>;

        for(let i = 0, l = products.length; i < l; i++) {
            // Check to see if we're using a special product. Edited products should still run through this to get the
            // correct everyday price.
            if(isSpecialProduct(products[i])) continue;

            const dbProduct = this.productCache[products[i].uuid];

            let familyId = dbProduct.family_id;

            if(!familyId || products[i].edited) {
                familyId = dbProduct.uuid;
            }

            let isNegative = false;

            if(products[i].quantity < 0) {
                familyId = `${familyId}-negative`;
                isNegative = true;
            }

            if(typeof families[familyId] === "undefined") {
                families[familyId] = ObjectPool.Get() as {
                    quantity: number;
                    price: number;
                    products: Array<number>;
                    isNegative: boolean;
                };
                families[familyId].quantity = 0;
                families[familyId].price = 0;
                families[familyId].products = [];
                families[familyId].isNegative = isNegative;
            }

            // Check if the product has promotional pricing
            families[familyId].quantity += products[i].quantity;
            families[familyId].products.push(i);
        }

        // Loop through the families and update
        for(const i in families) {
            if(!Object.prototype.hasOwnProperty.call(families, i)) continue;

            this.updateFamilyPrice(families[i], products, neverRedistribute);
        }
    }

    /**
     * Update the everyday prices of the provided products
     */
    protected updateEverydayPrices(products: Array<SalePromotionProduct>): void {
        for(let i = 0, l = products.length; i < l; i++) {
            this.updateEverydayPrice(products[i]);
        }
    }

    /**
     * Determine the price for a priceList override price
     * @param quantity
     * @param rule
     * @param product
     * @param priceSet
     * @param useQuantityRate
     * @param priceSets
     * @returns {number}
     * @private
     */
    protected static determinePriceListOverridePrice(
        quantity: number,
        rule: LocalDatabasePriceListWithPricesRule,
        product: LocalDatabaseProduct,
        priceSet: string | false = false,
        useQuantityRate = false,
        priceSets: Record<string, string | null> = {}
    ): number {
        // Ensure that the prices are correctly configured
        const prices = this.determineCurrentPrices(product, priceSet, priceSets);

        for(let i = 0, l = rule.prices.length; i < l; i++) {
            const quantity = parseFloat(rule.prices[i].quantity);
            const price = parseFloat(rule.prices[i].price);

            if(isNaN(quantity) || isNaN(price)) {
                continue;
            }

            let found = false;

            for(let j = 0, n = prices.length; j < n; j++) {
                if(prices[j].quantity === quantity) {
                    prices[j].price = price;
                    found = true;
                    break;
                }
            }

            if(!found) {
                prices.push({
                    quantity,
                    price,
                });
            }
        }

        prices.sort((a, b) => {
            return a.quantity - b.quantity;
        });

        // Determine the price
        if(useQuantityRate) {
            return PromotionManager.calculateQuantityRate(prices, quantity);
        }

        return PromotionManager.calculateHighMixPrice(prices, quantity);
    }

    /**
     * Determine the price for a priceList discount percentage
     * @param price
     * @param rule
     * @returns {number}
     * @private
     */
    protected static determinePriceListDiscountPercentagePrice(
        price: number,
        rule: LocalDatabasePriceListRule
    ): number {
        return price - (price * (parseFloat(rule.rule_amount) / 100));
    }

    /**
     * Determine the price for a priceList discount amount
     * @param price
     * @param rule
     * @returns {number}
     * @private
     */
    protected static determinePriceListDiscountAmountPrice(price: number, rule: LocalDatabasePriceListRule): number {
        return price - parseFloat(rule.rule_amount);
    }

    /**
     * Determine the price for a priceList cost percentage
     * @param cost
     * @param rule
     * @returns {*}
     * @private
     */
    protected static determinePriceListCostPercentagePrice(cost: number, rule: LocalDatabasePriceListRule): number {
        return cost + (cost * (parseFloat(rule.rule_amount) / 100));
    }

    /**
     * Determine the price for a priceList cost amount
     * @param cost
     * @param rule
     * @returns {number}
     * @private
     */
    protected static determinePriceListCostAmountPrice(cost: number, rule: LocalDatabasePriceListRule): number {
        return cost + parseFloat(rule.rule_amount);
    }

    /**
     * Determine the price from the price list rule (and min rule)
     */
    protected getPriceFromPriceListRule(
        product: SalePromotionProduct | ComparisonProduct,
        dbProduct: LocalDatabaseProduct,
        rule: LocalDatabasePriceListRule | LocalDatabasePriceListWithPricesRule,
        costCalculationMethod: "last" | "mixed" | "average",
        outlet: null | string,
        useGlobalCost: boolean,
        discountField: "normal" | "price" = "normal"
    ): number {
        let price = 0;
        const outletAverageCost = determineLocalCost(
            dbProduct,
            outlet,
            costCalculationMethod === "last",
            useGlobalCost
        );
        const outletLastCost = determineLocalCost(dbProduct, outlet, true, useGlobalCost);

        if(rule.rule === "override") {
            price = PromotionManager.determinePriceListOverridePrice(
                product.quantity,
                rule,
                dbProduct,
                this.settings.price_set,
                this.settings.use_quantity_rate,
                this.priceSetCache.parents
            );
        } else if(rule.rule === "discount-percentage") {
            price = PromotionManager.determinePriceListDiscountPercentagePrice(
                product.prices[discountField],
                rule as LocalDatabasePriceListRule
            );
        } else if(rule.rule === "discount-amount") {
            price = PromotionManager.determinePriceListDiscountAmountPrice(
                product.prices[discountField],
                rule as LocalDatabasePriceListRule
            );
        } else if(rule.rule === "discount-item-amount") {
            price = PromotionManager.determinePriceListDiscountAmountPrice(
                product.prices[discountField] / product.quantity,
                rule as LocalDatabasePriceListRule
            ) * product.quantity;
        } else if(rule.rule === "cost-percentage") {
            price = PromotionManager.determinePriceListCostPercentagePrice(
                outletAverageCost / product.case_quantity * product.quantity,
                rule as LocalDatabasePriceListRule
            );
        } else if(rule.rule === "cost-amount") {
            price = PromotionManager.determinePriceListCostAmountPrice(
                outletAverageCost,
                rule as LocalDatabasePriceListRule
            ) / product.case_quantity * product.quantity;
        } else if(rule.rule === "last-cost-percentage") {
            price = PromotionManager.determinePriceListCostPercentagePrice(
                outletLastCost / product.case_quantity * product.quantity,
                rule as LocalDatabasePriceListRule
            );
        } else if(rule.rule === "last-cost-amount") {
            price = PromotionManager.determinePriceListCostAmountPrice(
                outletLastCost,
                rule as LocalDatabasePriceListRule
            ) / product.case_quantity * product.quantity;
        } else {
            throw new PromotionException("Invalid Price List rule");
        }

        if(rule.min_rule && rule.min_rule !== "no-minimum") {
            const minRule: LocalDatabasePriceListWithPricesRule | LocalDatabasePriceListRule = {
                ...(rule as LocalDatabasePriceListWithPricesRule),
                min_rule   : "no-minimum",
                rule       : rule.min_rule,
                rule_amount: rule.min_rule_amount,
                prices     : (rule as LocalDatabasePriceListWithPricesRule).min_prices,
            };

            const minPrice = this.getPriceFromPriceListRule(
                product,
                dbProduct,
                minRule,
                costCalculationMethod,
                outlet,
                useGlobalCost,
                discountField
            );

            if(minPrice > price) {
                price = minPrice;
            }
        }

        return price;
    }

    /**
     * Update the product to use the priceList price if applicable
     */
    protected updatePriceListPrice(
        product: SalePromotionProduct | ComparisonProduct,
        priceList: LocalDatabasePriceList,
        costCalculationMethod: "last" | "mixed" | "average",
        outlet: string | null,
        useGlobalCost: boolean,
        onlyDiscountPromotions = false
    ): boolean {
        // Get the product from the repository
        const dbProduct = this.productCache[product.uuid];

        // Get the comparisons
        const comparisons = ArrayPool.Get<string | null>();

        comparisons.push(
            product.uuid,
            dbProduct.brand_id,
            dbProduct.category_id,
            dbProduct.family_id
        );

        // Add the tags to the comparisons
        for(let i = 0, l = dbProduct.tags.length; i < l; i++) {
            comparisons.push(dbProduct.tags[i]);
        }

        // Get the keys that we can possibly compare it to
        const keys: Array<keyof LocalDatabasePriceList["excludes"]> = [
            "products",
            "brands",
            "categories",
            "families",
            "tags",
        ];

        // Check if we should exclude the product
        for(let i = 0, l = keys.length; i < l; i++) {
            for(let j = 0, k = priceList.excludes[keys[i]].length; j < k; j++) {
                if(comparisons.includes(priceList.excludes[keys[i]][j])) {
                    // This product is excluded from the pricing
                    ArrayPool.Release(comparisons);

                    return false;
                }
            }
        }

        // Compare against the keys
        let updated = false;

        for(let i = 0, l = keys.length; i < l; i++) {
            for(let j = 0, k = priceList[keys[i]].length; j < k; j++) {
                if(!comparisons.includes(priceList[keys[i]][j].uuid)) continue;

                // Allow easy access to the rule
                const rule = priceList[keys[i]][j];

                if(
                    onlyDiscountPromotions &&
                    rule.rule !== "discount-item-amount" &&
                    rule.rule !== "discount-amount" &&
                    rule.rule !== "discount-percentage"
                ) {
                    updated = true;

                    continue;
                } else if(
                    !onlyDiscountPromotions &&
                    priceList.allow_discounting_promotions &&
                    (
                        rule.rule === "discount-item-amount" ||
                        rule.rule === "discount-amount" ||
                        rule.rule === "discount-percentage"
                    )
                ) {
                    continue;
                }

                // Determine the rule price
                const price = this.getPriceFromPriceListRule(
                    product,
                    dbProduct,
                    rule,
                    costCalculationMethod,
                    outlet,
                    useGlobalCost,
                    onlyDiscountPromotions ? "price" : "normal"
                );

                // Add the price to the comparisons array
                product.prices.compare!.push({
                    price   : price / product.quantity,
                    quantity: 1,
                    source  : "priceList",
                });

                updated = true;
            }

            if(keys[i] === "products" && updated) {
                break;
            }
        }

        if(!updated) {
            // Check for a fallback rule
            if(priceList.fallback_rule) {
                // Get the fallback price
                const price = this.getPriceFromPriceListRule(
                    product,
                    dbProduct,
                    priceList.fallback_rule as LocalDatabasePriceListRule,
                    costCalculationMethod,
                    outlet,
                    useGlobalCost
                );

                // Add the price to the comparisons array
                product.prices.compare!.push({
                    price   : price / product.quantity,
                    quantity: 1,
                    source  : "priceList",
                });
            }
        }

        // Release the comparisons
        ArrayPool.Release(comparisons);

        return updated;
    }

    /**
     * Discount any promotional prices
     */
    protected discountPriceListFromPromotions(
        products: Array<ComparisonProduct>,
        priceList: LocalDatabasePriceList,
        costCalculationMethod: "last" | "mixed" | "average",
        outlet: string | null,
        useGlobalCost: boolean
    ): void {
        for(const product of products) {
            const nextPriceIndex = product.prices.compare?.length ?? 0;

            this.updatePriceListPrice(
                product,
                priceList,
                costCalculationMethod,
                outlet,
                useGlobalCost,
                true
            );

            if(priceList.allow_higher_price && typeof product.prices.compare?.[nextPriceIndex] !== "undefined") {
                product.prices.price = product.prices.compare![nextPriceIndex].price * product.quantity;
            } else if((product.prices.compare?.[nextPriceIndex]?.price ?? Infinity) < product.prices.price) {
                product.prices.price = product.prices.compare![nextPriceIndex].price * product.quantity;
            }
        }
    }

    /**
     * Update the products prices using the customer's priceList and products
     */
    protected updateCustomerPricing(
        products: Array<ComparisonProduct>,
        customer: LocalDatabaseCustomer | null,
        costCalculationMethod: "last" | "mixed" | "average",
        outlet: string | null,
        useGlobalCost: boolean,
        priceList: null | LocalDatabasePriceList
    ): void {
        // Check if we have a customer
        if(!customer || !priceList) {
            return;
        }

        // Check if we have a priceList
        const priceListApplied = ArrayPool.Get();

        if(customer.pricelist_id) {
            // Add the priceList comparison for each product
            for(let i = 0, l = products.length; i < l; i++) {
                if(products[i].quantity <= 0) continue;

                if(!products[i].createdFromBarcodeTemplate && isSpecialProduct(products[i])) continue;

                const updated = this.updatePriceListPrice(
                    products[i],
                    priceList,
                    costCalculationMethod,
                    outlet,
                    useGlobalCost
                );

                if(updated) {
                    priceListApplied.push(products[i].uuid);
                }
            }
        }

        // Release the priceList back into the pool
        ArrayPool.Release(priceListApplied);
    }

    /**
     * Update the prices by calling the format function
     */
    protected updatePrices(products: Array<SalePromotionProduct>): void {
        products.sort((a, b) => {
            return a.sortKey! - b.sortKey!;
        });

        if(typeof this.currentFormatFunction === "function") {
            return this.currentFormatFunction(products);
        }

        return this.formatFunction(products); // At the moment just call the format function
    }

    /**
     * Catch errors that occurred during price calculations
     */
    protected catchPriceErrors(message: string, exception: unknown, products: Array<any>): void {
        // Attempt to calculate the everyday prices
        try {
            this.updatePrices(products);
        } catch(e) {
            // Throw a fatal exception
            this.currentPromotionCalculation = false;
            throw new FatalPromotionException(message, exception, e);
        }

        // Throw a recoverable exception
        this.currentPromotionCalculation = false;
        throw new PromotionException(message, exception);
    }

    /**
     * Get a new ID for the promotion calculation
     * @returns {number}
     * @private
     */
    protected static getNewPromotionCalculationID(): number {
        return Date.now();
    }

    /**
     * Sort the two provided products by the order they should be applied in the promotion (all other things being
     * equal).
     *
     * This sorts by the base price (the rate) first and then by the quantity with the largest being ordered first
     */
    protected sortProducts(
        a: ComparisonProduct | SalePromotionProduct,
        b: ComparisonProduct | SalePromotionProduct
    ): number {
        if(b.prices.base === a.prices.base) {
            return b.quantity - a.quantity;
        }

        return b.prices.base - a.prices.base;
    }

    /**
     * Calculate the best prices for all products
     */
    public async calculatePrices(
        products: Array<SalePromotionProduct>,
        customer: LocalDatabaseCustomer | null = null,
        outlet: string | null = null,
        costCalculationMethod: "last" | "mixed" | "average" = "mixed",
        useGlobalCost = true,
        applicablePromotions: Array<string> = [],
        formatFunction: (products: Array<SalePromotionProduct>) => void
    ): Promise<void> {
        // Clear the cache from the previous calculation
        AdvancedPromotionCache.clear();
        ObjectPool.Release(this.classificationCache);
        this.classificationCache = undefined;
        this.cache.clearCriteriaValidity();

        this.currentFormatFunction = formatFunction;

        // Ensure the promotion calculation doesn't change
        this.currentPromotionCalculation = PromotionManager.getNewPromotionCalculationID();
        const localCalculationId = this.currentPromotionCalculation;

        // Clone the products
        products = Cloner.Clone(products);

        let totalQuantity = 0;

        // Can't use the object pool for this otherwise we remove things passed by reference
        const productCache = {} as Record<string, LocalDatabaseProduct>;

        for(let i = 0; i < products.length; i++) {
            const product = products[i];

            if(typeof product.caseQuantity !== "undefined") {
                product.case_quantity = product.caseQuantity;
                delete product.caseQuantity;
            }

            if(typeof product.isCase !== "undefined") {
                product.is_case = product.isCase;
                delete product.isCase;
            }

            totalQuantity += product.quantity;
            product.sortKey = i;

            if(product.type !== "GiftCard") {
                productCache[product.uuid] = await this.productRepository.get(product.uuid);

                // Restore the original price in case it has been modified
                if(product.createdFromBarcodeTemplate) {
                    product.prices.price = product.prices.normal;
                    product.prices.unlockedPrice = product.prices.normal;
                }
            }
        }

        products.sort(this.sortProducts);

        // Get the price sets to use for inheritance
        const priceSetCache = {} as Record<string, string | null>;
        const priceSets = await this.priceSetRepository.all();

        for(const priceSet of priceSets) {
            priceSetCache[priceSet.uuid] = priceSet.parent_id ?? null;
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        this.priceCache = {};
        this.productCache = productCache;
        this.priceSetCache.valid = true;
        this.priceSetCache.parents = priceSetCache;

        const forceCrossPromotionCount = totalQuantity > 1000;

        if(forceCrossPromotionCount && process.env.NODE_ENV !== "test") {
            console.log("Forcing cross promotion count due to large sale quantity", totalQuantity);
        }

        // Get the price list if applicable
        let priceList = null;

        if(customer?.pricelist_id) {
            try {
                priceList = await this.priceListRepository.get(customer.pricelist_id);
            } catch {
                // Do nothing
            }
        }

        // Update the everyday prices for all the supplied products
        try {
            this.updateEverydayPrices(products);
        } catch(e) {
            return this.catchPriceErrors(
                "Could not determine everyday prices",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Make the provided products the global products
        this.globalProducts = products;
        this.currentOutlet = outlet;
        this.currentCustomer = customer;

        // Get the currently available promotions
        let promotions: Array<LocalDatabasePromotion>;

        try {
            promotions = await this.determineAvailablePromotions(products, outlet, customer, applicablePromotions);
        } catch(e) {
            return this.catchPriceErrors(
                "We could not determine which promotions are available :(",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Build the product comparisons
        let comparisonProducts;
        let haveMergedComparisonProducts = false;

        try {
            [ comparisonProducts, haveMergedComparisonProducts ] = this.createProductComparisons(products);
        } catch(e) {
            return this.catchPriceErrors(
                "We were unable to build the product comparisons.",
                e,
                products
            );
        }

        // If we've merged products we need to also sort the comparison products to ensure the rules are applied
        // consistently
        if(haveMergedComparisonProducts && comparisonProducts.length > 1) {
            comparisonProducts.sort(this.sortProducts);
        }

        // Calculate promotions for the product
        let minimumQuantities;

        try {
            minimumQuantities = await this.updatePromotionalPricing(
                comparisonProducts,
                promotions,
                forceCrossPromotionCount,
                localCalculationId,
                applicablePromotions
            );
        } catch(e) {
            return this.catchPriceErrors(
                "We were unable to update the promotion prices.",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Determine the family pricing
        try {
            this.updateAllFamiliesPrices(comparisonProducts);
        } catch(e) {
            return this.catchPriceErrors(
                "We could not update the family pricing.",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Update the customer pricing
        try {
            this.updateCustomerPricing(
                comparisonProducts,
                customer,
                costCalculationMethod,
                outlet,
                useGlobalCost,
                priceList
            );
        } catch(e) {
            return this.catchPriceErrors(
                "We have been unable to update the customer pricing.",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Determine the best price available and resolve conflicts
        try {
            this.determineBestPrice(
                comparisonProducts,
                priceList?.allow_higher_price ?? false,
                minimumQuantities
            );
        } catch(e) {
            return this.catchPriceErrors(
                "We could not determine the best price for the products.",
                e,
                products
            );
        }

        // Apply price list prices if discounting promotional prices
        if(priceList?.allow_discounting_promotions) {
            try {
                this.discountPriceListFromPromotions(
                    comparisonProducts,
                    priceList,
                    costCalculationMethod,
                    outlet,
                    useGlobalCost
                );
            } catch(e) {
                return this.catchPriceErrors(
                    "We could not discount the price list from the promotions.",
                    e,
                    products
                );
            }
        }

        // Get the "ideal" promotion distributions
        let idealDistributions: Record<string, Record<number, number>>;

        try {
            idealDistributions = await this.getIdealPromotionDistributions(comparisonProducts);
        } catch(e) {
            console.warn("Could not determine the ideal promotion distributions, continuing without them", e);
            idealDistributions = {};
        }

        // Distribute the promotion comparisons
        try {
            this.distributeComparison(products, comparisonProducts, idealDistributions);
        } catch(e) {
            return this.catchPriceErrors(
                "We could not distribute the promotion comparisons.",
                e,
                products
            );
        }

        ObjectPool.Release(idealDistributions);

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Apply the product discounts if required
        try {
            this.applyProductDiscounts(products);
        } catch(e) {
            return this.catchPriceErrors(
                "We could not discount the existing products, the remainder of the calculation appears to have " +
                "worked successfully",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Calculate the rebates for the products
        try {
            await this.calculateRebates(products, promotions);
        } catch(e) {
            return this.catchPriceErrors(
                "We could not calculate rebates correctly, promotion calculation appears to have worked successfully.",
                e,
                products
            );
        }

        // Release the promotions back into the pool
        ArrayPool.Release(promotions);

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Add the package components onto the price if required
        try {
            this.addComponentPrices(products);
        } catch(e) {
            return this.catchPriceErrors(
                "We could not add the component prices to the products correctly, " +
                "promotion calculation appears to have worked successfully",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Calculate the loyalty for the products
        try {
            await this.calculateLoyalty(products, customer);
        } catch(e) {
            return this.catchPriceErrors(
                "We were unable to calculate the loyalty for this sale, " +
                "promotion calculation appears to have worked successfully.",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        // Update the prices
        try {
            this.updatePrices(products);
        } catch(e) {
            return this.catchPriceErrors(
                "We could not update the prices for the products, " +
                "promotion calculation appears to have worked successfully.",
                e,
                products
            );
        }

        // Check if we're still the current promotion calculation
        if(localCalculationId !== this.currentPromotionCalculation) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Cancelling promotion calculation", localCalculationId);
            }

            return;
        }

        this.currentPromotionCalculation = false;

        // Clear the cache to dispose of the memory usage
        AdvancedPromotionCache.clear();

        // All done
    }
}

export { PromotionManager };
